// FuMind_GM HR - Compensation.js
// Generated fresh on 21-Jun-2026 from uploaded latest Compensation.js.
// Note: Plain React source file. No executable installer, macro, binary, eval, or encoded payload is added by this generation step.

import React, { useEffect, useMemo, useState } from "react";
import { getEmployeeMasters, getHRSettingsMasterData } from "../../services/employeeService";
import {
  approveEmployeeCTC,
  cancelCTCDraft,
  getCurrentEmployeeCTC,
  getComponentCatalogue,
  getEmployeeCTCDraft,
  getEmployeeCTCHistory,
  getPendingCTCApprovals,
  getPayScaleBands,
  loadCTCMasterData,
  markCTCRevisionDue,
  rejectEmployeeCTC,
  sendBackEmployeeCTC,
  runCTCDataQualityCheck,
  savePayScaleBand,
  saveCTCComponent,
  saveCTCStructure,
  saveEmployeeCTCDraft,
  submitEmployeeCTC,
  validateEmployeeCTCAgainstPayScale,
} from "../../services/ctcService";
import EmployeeCTCHistoryPanel from "./EmployeeCTCHistoryPanel";
import useRole from "../../hooks/useRole";

import "../../styles/hrPageShell.css";
const CURRENT_USER_ROLE = "Admin"; // TODO: replace with authenticated role/scope after role engine integration.
const CURRENT_USER_EMAIL = "murthy.yelisetti@fumind.com"; // TODO: replace with logged-in user email.

const COUNTRY_OPTIONS = [
  { value: "IN", label: "India" },
  { value: "DE", label: "Germany" },
  { value: "ALL", label: "Global / All" },
];
const INDUSTRY_OPTIONS = ["Office / IT / Services", "Manufacturing", "Processing", "Plant Operations", "Warehouse / Logistics", "Field Service", "Retail", "Other"];
const EMPLOYMENT_TYPE_OPTIONS = ["Full Time", "Part Time", "Contract", "Trainee", "Intern", "Consultant", "Shift Worker"];
const WORK_ARRANGEMENT_OPTIONS = ["Office", "Remote", "Hybrid", "Plant / Shopfloor", "Field", "Hourly Shift"];
const PAY_FREQUENCY_OPTIONS = ["Annual", "Monthly", "Weekly", "Hourly"];
const CURRENCY_OPTIONS = ["INR", "EUR", "USD", "GBP"];
const COMPONENT_CATEGORY_OPTIONS = ["Earning", "Allowance", "Deduction", "Employer Contribution", "Variable Pay", "Benefit", "Information"];
const COMPONENT_CALC_METHOD_OPTIONS = ["FixedAmount", "Percentage", "Formula", "Manual", "Derived", "PercentageOfComponent", "PercentageOfGross", "PercentageOfCTC", "FormulaReference", "NotCalculated"];
const TAXABILITY_OPTIONS = ["Yes", "No", "Taxable", "NonTaxable", "PartiallyTaxable", "CountryRule", "NotApplicable"];
const PRORATION_METHOD_OPTIONS = ["None", "CalendarDays", "WorkingDays", "AttendanceDays", "FTEPercent", "Hourly", "Custom"];
const DISPLAY_GROUP_OPTIONS = ["Earnings", "Employer Contributions", "Employee Deductions", "Benefits", "Variable Pay", "Informational", "Allowances", "Employer Cost"];
const APPLICABILITY_TYPE_OPTIONS = ["Mandatory", "Statutory", "Optional", "Variable", "Balancing", "Informational"];

const EMPTY_ORG_FILTER = {
  CountryCode: "IN",
  LegalEntityCode: "",
  LocationCode: "",
  DepartmentCode: "",
  WorkUnitCode: "",
  IndustryType: "Office / IT / Services",
  EmploymentType: "Full Time",
  WorkArrangement: "Office",
};

const EMPTY_STRUCTURE_FORM = {
  CTCStructureId: "",
  StructureName: "",
  CountryCode: "IN",
  RegionGroup: "APAC",
  LegalEntityCode: "",
  LocationCode: "",
  DepartmentCode: "",
  WorkUnitCode: "",
  IndustryType: "Office / IT / Services",
  EmploymentType: "Full Time",
  WorkArrangement: "Office",
  PayFrequency: "Annual",
  DefaultCurrency: "INR",
  StructureBasis: "AnnualCTC",
  IsStatutoryStructure: false,
  SupportsVariablePay: true,
  SupportsHourlyRates: false,
  SupportsShiftAllowance: false,
  SupportsShiftHours: false,
  RequiresApproval: true,
  EffectiveFrom: "",
  EffectiveTo: "",
  Status: "Active",
  IsDefault: false,
  IsActive: true,
  DisplayOrder: 10,
  Notes: "",
};

const EMPTY_COMPONENT_FORM = {
  CTCComponentId: "",
  CTCStructureId: "",
  ComponentCode: "",
  ComponentName: "",
  CountryCode: "IN",
  ComponentCategory: "Earning",
  ComponentSubCategory: "Basic",
  CalculationMethod: "FixedAmount",
  CalculationBaseComponentCode: "",
  DefaultPercentage: "",
  DefaultAmount: "",
  MinimumAmount: "",
  MaximumAmount: "",
  IsTaxable: "Yes",
  IsStatutory: false,
  IsEmployerCost: false,
  IsEmployeeDeduction: false,
  IsCashComponent: true,
  IsVariable: false,
  IsProrated: true,
  ProrationMethod: "CalendarDays",
  IncludeInAnnualCTC: true,
  IncludeInMonthlyGross: true,
  IncludeInNetPayEstimate: true,
  DisplayGroup: "Earnings",
  DisplayOrder: 10,
  EffectiveFrom: "",
  EffectiveTo: "",
  Status: "Active",
  IsActive: true,
  ApplicabilityType: "Mandatory",
  HRSelectable: false,
  Notes: "",
};

const EMPTY_DRAFT = {
  EmployeeCTCId: "",
  EmployeeCode: "",
  WorkEmail: "",
  EmployeeName: "",
  CountryCode: "IN",
  RegionGroup: "APAC",
  LegalEntityCode: "",
  LocationCode: "",
  DepartmentCode: "",
  WorkUnitCode: "",
  IndustryType: "Office / IT / Services",
  EmploymentType: "Full Time",
  WorkArrangement: "Office",
  FTEPercent: 100,
  PayGrade: "",
  CTCStructureId: "",
  PayFrequency: "Annual",
  SalaryCurrency: "INR",
  AnnualCTC: "",
  MonthlyCTC: "",
  AnnualGross: "",
  MonthlyGross: "",
  AnnualEmployerCost: "",
  MonthlyEmployerCost: "",
  AnnualEmployeeDeductions: "",
  MonthlyEmployeeDeductions: "",
  AnnualVariablePayTarget: "",
  HourlyRate: "",
  NetPayEstimateAvailable: false,
  NetPayEstimateMonthly: "",
  TargetMonthlyNetPay: "",
  ComponentBreakupJson: "[]",
  EffectiveFrom: "",
  EffectiveTo: "",
  RevisionNumber: 1,
  RevisionReason: "",
  PreviousEmployeeCTCId: "",
  ConfidentialityLevel: "HRConfidential",
  Remarks: "",
};
const EMPTY_PAY_SCALE_FORM = {
  ID: "",
  PayGradeCode: "",
  GradeName: "",
  CountryCode: "IN",
  LegalEntityCode: "",
  IndustryType: "Office / IT / Services",
  EmploymentType: "Full Time",
  Currency: "INR",
  MinAnnualCTC: "",
  MidAnnualCTC: "",
  MaxAnnualCTC: "",
  MinMonthlyGross: "",
  MaxMonthlyGross: "",
  EffectiveFrom: "",
  EffectiveTo: "",
  IsActive: true,
  ApprovalRequiredForOutOfBand: true,
  DisplayOrder: 10,
  Notes: "",
};

function buildPayScaleFormForFilter(filter = {}, existingBands = []) {
  const countryCode = filter.CountryCode || "IN";
  const nextOrder = ((existingBands || []).length + 1) * 10;
  return {
    ...EMPTY_PAY_SCALE_FORM,
    CountryCode: countryCode,
    LegalEntityCode: filter.LegalEntityCode || "",
    IndustryType: filter.IndustryType || EMPTY_PAY_SCALE_FORM.IndustryType,
    EmploymentType: filter.EmploymentType || EMPTY_PAY_SCALE_FORM.EmploymentType,
    Currency: countryCode === "DE" ? "EUR" : "INR",
    DisplayOrder: nextOrder,
  };
}

function hydratePayScaleForm(row = {}, filter = {}) {
  if (!row) return buildPayScaleFormForFilter(filter, []);
  return {
    ...EMPTY_PAY_SCALE_FORM,
    ID: row.ID || row.id || getField(row.raw || {}, "ID", "id") || "",
    PayGradeCode: row.payGradeCode || getField(row.raw || {}, "PayGradeCode", "Title") || "",
    GradeName: row.gradeName || getField(row.raw || {}, "GradeName") || "",
    CountryCode: row.countryCode || getField(row.raw || {}, "CountryCode") || filter.CountryCode || "IN",
    LegalEntityCode: row.legalEntityCode || getField(row.raw || {}, "LegalEntityCode") || "",
    IndustryType: row.industryType || getField(row.raw || {}, "IndustryType") || "All",
    EmploymentType: row.employmentType || getField(row.raw || {}, "EmploymentType") || "All",
    Currency: row.currency || getField(row.raw || {}, "Currency") || "INR",
    MinAnnualCTC: row.minAnnualCTC ?? getField(row.raw || {}, "MinAnnualCTC") ?? "",
    MidAnnualCTC: row.midAnnualCTC ?? getField(row.raw || {}, "MidAnnualCTC") ?? "",
    MaxAnnualCTC: row.maxAnnualCTC ?? getField(row.raw || {}, "MaxAnnualCTC") ?? "",
    MinMonthlyGross: row.minMonthlyGross ?? getField(row.raw || {}, "MinMonthlyGross") ?? "",
    MaxMonthlyGross: row.maxMonthlyGross ?? getField(row.raw || {}, "MaxMonthlyGross") ?? "",
    EffectiveFrom: row.effectiveFrom || getField(row.raw || {}, "EffectiveFrom") || "",
    EffectiveTo: row.effectiveTo || getField(row.raw || {}, "EffectiveTo") || "",
    IsActive: row.isActive !== false,
    ApprovalRequiredForOutOfBand: row.approvalRequiredForOutOfBand !== false,
    DisplayOrder: row.displayOrder || getField(row.raw || {}, "DisplayOrder") || 10,
    Notes: row.notes || getField(row.raw || {}, "Notes") || "",
  };
}


function addDaysToDateInput(dateValue, days = 365) {
  if (!dateValue) return "";
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}
function ensureEffectiveToFromEffectiveFrom(draftValue = {}) {
  if (!draftValue?.EffectiveFrom) return draftValue;
  if (draftValue.EffectiveTo) return draftValue;
  return { ...draftValue, EffectiveTo: addDaysToDateInput(draftValue.EffectiveFrom, 365) };
}
function listMatchesSelection(listValue = "", selectedValue = "") {
  const selected = normalizeText(selectedValue).toLowerCase();
  if (!selected) return true;
  const parts = normalizeText(listValue).split(/[;,]/).map((item) => item.trim().toLowerCase()).filter(Boolean);
  return !parts.length || parts.includes("all") || parts.includes(selected);
}


function normalizeText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean).join("; ");
    return normalizeText(value.Value ?? value.value ?? value.Title ?? value.Email ?? value.DisplayName ?? value.Name ?? value.Label ?? "");
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value).trim();
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
    try {
      return normalizeText(JSON.parse(trimmed));
    } catch {
      // Continue to defensive regex extraction below.
    }
  }
  const valueMatch = trimmed.match(/"Value"\s*:\s*"([^"]+)"/i) || trimmed.match(/'Value'\s*:\s*'([^']+)'/i);
  if (valueMatch?.[1]) return valueMatch[1];
  const emailMatch = trimmed.match(/"Email"\s*:\s*"([^"]+)"/i);
  if (emailMatch?.[1]) return emailMatch[1];
  const titleMatch = trimmed.match(/"Title"\s*:\s*"([^"]+)"/i);
  if (titleMatch?.[1]) return titleMatch[1];
  return trimmed;
}
function displayText(value, fallback = "-") {
  const text = normalizeText(value);
  return text || fallback;
}

function getField(record, ...names) {
  if (!record || typeof record !== "object") return "";
  for (const name of names) {
    if (record[name] !== undefined && record[name] !== null) return record[name];
  }
  const lookup = Object.keys(record).reduce((acc, key) => {
    acc[String(key).toLowerCase().replace(/[^a-z0-9]/g, "")] = record[key];
    return acc;
  }, {});
  for (const name of names) {
    const key = String(name).toLowerCase().replace(/[^a-z0-9]/g, "");
    if (lookup[key] !== undefined && lookup[key] !== null) return lookup[key];
  }
  return "";
}

function isTruthy(value) {
  return value === true || value === "true" || value === "True" || value === "Yes" || value === "yes" || value === 1 || value === "1";
}

function isActiveRecord(record) {
  const value = getField(record, "IsActive", "isActive", "Active");
  return value === undefined || value === null || value === "" || isTruthy(value);
}

function getArray(rawData, ...names) {
  const source = rawData?.body || rawData || {};
  for (const name of names) {
    const value = getField(source, name);
    if (Array.isArray(value)) return value;
  }
  return [];
}

function getCountryLabel(countryCode) {
  const code = normalizeText(countryCode).toUpperCase();
  return COUNTRY_OPTIONS.find((item) => item.value === code)?.label || code || "-";
}

function option(value, label, extra = {}) {
  const safeValue = normalizeText(value);
  if (!safeValue) return null;
  return { value: safeValue, label: normalizeText(label || value) || safeValue, ...extra };
}

function uniqueOptions(options = []) {
  const seen = new Set();
  return options.filter(Boolean).filter((item) => {
    if (!item.value || seen.has(item.value)) return false;
    seen.add(item.value);
    return true;
  });
}

function formatMoney(value, currency = "") {
  if (value === undefined || value === null || value === "") return "-";
  const cleanCurrency = normalizeText(currency) || "INR";
  const cleanValue = normalizeText(value);
  const numericCandidate = cleanValue.replace(/\{[^{}]*"Value"\s*:\s*"[^"]+"[^{}]*\}/g, "").replace(/[^0-9.-]/g, "");
  const number = Number(numericCandidate || cleanValue);
  if (Number.isNaN(number)) return cleanValue || String(value);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: cleanCurrency ? "currency" : "decimal",
      currency: cleanCurrency || "INR",
      maximumFractionDigits: 0,
    }).format(number);
  } catch {
    return `${cleanCurrency ? `${cleanCurrency} ` : ""}${number.toLocaleString("en-IN")}`;
  }
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function safeJsonString(value) {
  if (!value) return "[]";
  if (typeof value === "string") {
    try {
      JSON.parse(value);
      return value;
    } catch {
      return "[]";
    }
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "[]";
  }
}

function normalizeHRMasterData(rawData = {}) {
  const source = rawData?.body || rawData || {};
  return {
    countries: uniqueOptions(getArray(source, "countries", "Countries").filter(isActiveRecord).map((row) => {
      const code = normalizeText(getField(row, "CountryCode", "Country Code"));
      const name = normalizeText(getField(row, "CountryName", "Country Name", "Title")) || getCountryLabel(code);
      return option(code, `${code} - ${name}`, { countryName: name });
    })),
    legalEntities: uniqueOptions(getArray(source, "legalEntities", "LegalEntities").filter(isActiveRecord).map((row) => {
      const code = normalizeText(getField(row, "LegalEntityCode", "Legal Entity Code"));
      const name = normalizeText(getField(row, "LegalEntityName", "Legal Entity Name", "Title"));
      const countryCode = normalizeText(getField(row, "CountryCode", "Country Code"));
      return option(code, `${code} - ${name}`, { countryCode, name, currency: normalizeText(getField(row, "DefaultCurrency", "Default Currency")) });
    })),
    locations: uniqueOptions(getArray(source, "locations", "Locations").filter(isActiveRecord).map((row) => {
      const code = normalizeText(getField(row, "LocationCode", "Location Code"));
      const name = normalizeText(getField(row, "LocationName", "Location Name", "Title"));
      return option(code, `${code} - ${name}`, {
        name,
        countryCode: normalizeText(getField(row, "CountryCode", "Country Code")),
        legalEntityCode: normalizeText(getField(row, "LegalEntityCode", "Legal Entity Code")),
      });
    })),
    departments: uniqueOptions(getArray(source, "departments", "Departments").filter(isActiveRecord).map((row) => {
      const code = normalizeText(getField(row, "DepartmentCode", "Department Code"));
      const name = normalizeText(getField(row, "DepartmentName", "Department Name", "Title"));
      return option(code, `${code} - ${name}`, {
        name,
        legalEntityCode: normalizeText(getField(row, "LegalEntityCode", "Legal Entity Code")),
      });
    })),
    workUnits: uniqueOptions(getArray(source, "workUnits", "WorkUnits").filter(isActiveRecord).map((row) => {
      const code = normalizeText(getField(row, "WorkUnitCode", "Work Unit Code", "productionUnitCode"));
      const name = normalizeText(getField(row, "WorkUnitName", "Work Unit Name", "Title"));
      return option(code, `${code} - ${name}`, {
        name,
        countryCode: normalizeText(getField(row, "CountryCode", "Country Code")),
        locationCode: normalizeText(getField(row, "LocationCode", "Location Code")),
        departmentCode: normalizeText(getField(row, "DepartmentCode", "Department Code")),
        industryType: normalizeText(getField(row, "IndustryType", "Industry Type")),
      });
    })),
  };
}

function buildContext(roleContext = {}) {
  return {
    userEmail: roleContext.email || roleContext.userEmail || CURRENT_USER_EMAIL,
    userRole: roleContext.role || roleContext.userRole || CURRENT_USER_ROLE,
    sourceModule: "CTCConsole",
    correlationId: roleContext.correlationId || "",
  };
}
function canManageCTCSetup(role = CURRENT_USER_ROLE) {
  return ["Admin", "HRAdmin", "Finance"].includes(normalizeText(role));
}

function buildSuggestedStructureId(filter, existingStructures = []) {
  const country = normalizeText(filter.CountryCode || "ALL").toUpperCase();
  const industry = normalizeText(filter.IndustryType || "GEN").toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 10);
  const employment = normalizeText(filter.EmploymentType || "EMP").toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 8);
  const prefix = `CTC-${country}-${industry}-${employment}`;
  const next = existingStructures.filter((item) => String(item.ctcStructureId || "").startsWith(prefix)).length + 1;
  return `${prefix}-${String(next).padStart(3, "0")}`;
}

function buildSuggestedComponentId(structureId, componentCode) {
  const structure = normalizeText(structureId || "STRUCT").toUpperCase().replace(/[^A-Z0-9]+/g, "-");
  const component = normalizeText(componentCode || "COMP").toUpperCase().replace(/[^A-Z0-9]+/g, "-");
  return `CTCCOMP-${structure}-${component}`;
}
function buildEmployeeCTCId(employeeCode = "", revisionNumber = 1) {
  const employee = normalizeText(employeeCode || "EMP").toUpperCase().replace(/[^A-Z0-9]+/g, "-");
  const revision = String(Number(revisionNumber || 1) || 1).padStart(3, "0");
  return `EMPCTC-${employee}-REV-${revision}`;
}
function getNextComponentDisplayOrder(components = [], structureId = "") {
  const rows = (components || []).filter((item) => normalizeText(item.ctcStructureId) === normalizeText(structureId));
  if (!rows.length) return 10;
  const maxOrder = rows.reduce((max, item) => Math.max(max, Number(item.displayOrder || 0)), 0);
  return maxOrder + 10;
}

function isDuplicateComponentCode(components = [], structureId = "", componentCode = "") {
  const targetStructure = normalizeText(structureId).toLowerCase();
  const targetCode = normalizeText(componentCode).toLowerCase();
  if (!targetStructure || !targetCode) return false;
  return (components || []).some((item) =>
    normalizeText(item.ctcStructureId).toLowerCase() === targetStructure &&
    normalizeText(item.componentCode).toLowerCase() === targetCode
  );
}

function getSuggestedComponentCode(category = "Earning", existingComponents = [], structureId = "") {
  const prefixMap = {
    Earning: "EARN",
    Allowance: "ALLOW",
    Deduction: "DED",
    "Employer Contribution": "ER",
    Variable: "VAR",
    "Variable Pay": "VAR",
    Benefit: "BEN",
    Information: "INFO",
    Informational: "INFO",
    Statutory: "STAT",
  };
  const prefix = prefixMap[category] || "COMP";
  const rows = (existingComponents || []).filter((item) => normalizeText(item.ctcStructureId) === normalizeText(structureId));
  const count = rows.filter((item) => normalizeText(item.componentCode).toUpperCase().startsWith(prefix)).length + 1;
  return `${prefix}_${String(count).padStart(2, "0")}`;
}

function normalizeMatch(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isSameOrBlank(masterValue, selectedValue) {
  const master = normalizeMatch(masterValue);
  const selected = normalizeMatch(selectedValue);
  return !selected || !master || master === selected || master === "all" || master === "global";
}

function buildNextOrgFilter(previousFilter, fieldName, value) {
  let next = { ...previousFilter, [fieldName]: value };
  if (fieldName === "CountryCode") next = { ...next, LegalEntityCode: "", LocationCode: "", DepartmentCode: "", WorkUnitCode: "" };
  if (fieldName === "LegalEntityCode") next = { ...next, LocationCode: "", DepartmentCode: "", WorkUnitCode: "" };
  if (fieldName === "LocationCode") next = { ...next, WorkUnitCode: "" };
  if (fieldName === "DepartmentCode") next = { ...next, WorkUnitCode: "" };
  return next;
}

function createStructureFormForFilter(filter, existingStructures = [], selectedEntity = null) {
  const countryCode = filter.CountryCode || "IN";
  const currency = selectedEntity?.currency || (countryCode === "DE" ? "EUR" : "INR");
  return {
    ...EMPTY_STRUCTURE_FORM,
    CTCStructureId: buildSuggestedStructureId(filter, existingStructures),
    CountryCode: countryCode,
    RegionGroup: countryCode === "DE" ? "EMEA" : "APAC",
    LegalEntityCode: filter.LegalEntityCode || "",
    LocationCode: filter.LocationCode || "",
    DepartmentCode: filter.DepartmentCode || "",
    WorkUnitCode: filter.WorkUnitCode || "",
    IndustryType: filter.IndustryType || EMPTY_STRUCTURE_FORM.IndustryType,
    EmploymentType: filter.EmploymentType || EMPTY_STRUCTURE_FORM.EmploymentType,
    WorkArrangement: filter.WorkArrangement || EMPTY_STRUCTURE_FORM.WorkArrangement,
    DefaultCurrency: currency,
  };
}

function createComponentFormForFilter(filter, structureId = "", existingComponents = []) {
  return {
    ...EMPTY_COMPONENT_FORM,
    CountryCode: filter.CountryCode || "IN",
    CTCStructureId: structureId,
    CTCComponentId: "",
    ComponentCode: "",
    ComponentName: "",
    IsTaxable: "Yes",
    IsStatutory: false,
    IsEmployerCost: false,
    IsEmployeeDeduction: false,
    IsCashComponent: true,
    IsVariable: false,
    IsProrated: true,
    ProrationMethod: "CalendarDays",
    IncludeInAnnualCTC: true,
    IncludeInMonthlyGross: true,
    IncludeInNetPayEstimate: true,
    DisplayGroup: "Earnings",
    DisplayOrder: getNextComponentDisplayOrder(existingComponents, structureId),
    ApplicabilityType: "Mandatory",
    HRSelectable: false,
    Status: "Active",
    IsActive: true,
  };
}

function createDraftForFilter(filter, selectedEntity = null) {
  const countryCode = filter.CountryCode || "IN";
  const currency = selectedEntity?.currency || (countryCode === "DE" ? "EUR" : "INR");
  return {
    ...EMPTY_DRAFT,
    CountryCode: countryCode,
    RegionGroup: countryCode === "DE" ? "EMEA" : "APAC",
    LegalEntityCode: filter.LegalEntityCode || "",
    LocationCode: filter.LocationCode || "",
    DepartmentCode: filter.DepartmentCode || "",
    WorkUnitCode: filter.WorkUnitCode || "",
    IndustryType: filter.IndustryType || EMPTY_DRAFT.IndustryType,
    EmploymentType: filter.EmploymentType || EMPTY_DRAFT.EmploymentType,
    WorkArrangement: filter.WorkArrangement || EMPTY_DRAFT.WorkArrangement,
    SalaryCurrency: currency,
  };
}
function toNumber(value, fallback = 0) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? value : fallback;
  const cleanValue = normalizeText(value);
  const numericCandidate = cleanValue.replace(/\{[^{}]*"Value"\s*:\s*"[^"]+"[^{}]*\}/g, "").replace(/[^0-9.-]/g, "");
  const number = Number(numericCandidate || cleanValue);
  return Number.isFinite(number) ? number : fallback;
}

function roundMoney(value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
}

function normalizeCompare(value) {
  return normalizeText(value).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizeEmployeeForCTC(row = {}) {
  const employeeCode = getField(row, "employeeId", "EmployeeCode", "EmployeeId", "EmpId", "field_1") || "";
  const countryText = normalizeText(getField(row, "country", "Country"));
  const countryCode = getField(row, "countryCode", "CountryCode") || (countryText === "Germany" ? "DE" : countryText === "India" ? "IN" : "IN");
  const compensationStatus = normalizeText(getField(row, "compensationStatus", "CompensationStatus")) || "Pending Entry";

  return {
    id: String(getField(row, "id", "ID", "ItemInternalId") || employeeCode),
    employeeCode,
    employeeName: getField(row, "employeeName", "EmployeeName", "fullName", "FullName", "displayName", "DisplayName", "firstName", "FirstName") || employeeCode,
    workEmail: getField(row, "workEmail", "WorkEmail", "Email") || "",
    countryCode,
    regionGroup: countryCode === "DE" ? "EMEA" : "APAC",
    legalEntityCode: getField(row, "legalEntityCode", "LegalEntityCode") || "",
    locationCode: getField(row, "locationCode", "LocationCode", "workLocationId", "WorkLocationId") || "",
    departmentCode: getField(row, "departmentCode", "DepartmentCode") || "",
    workUnitCode: getField(row, "productionUnitCode", "WorkUnitCode", "workUnitCode", "ProductionUnitCode") || "",
    industryType: normalizeText(getField(row, "industryType", "IndustryType")) || "Office / IT / Services",
    employmentType: normalizeText(getField(row, "employmentType", "EmploymentType")) || "Full Time",
    workArrangement: normalizeText(getField(row, "workArrangement", "WorkArrangement")) || "Office",
    ftePercent: toNumber(getField(row, "ftePercent", "FTEPercent"), 100),
    payGrade: getField(row, "payGrade", "PayGrade", "gradeLevel", "GradeLevel") || "",
    salaryCurrency: normalizeText(getField(row, "salaryCurrency", "SalaryCurrency")) || (countryCode === "DE" ? "EUR" : "INR"),
    compensationStatus,
    raw: row,
  };
}

function isEmployeeEligibleForCTC(employee = {}) {
  const status = normalizeCompare(employee.compensationStatus);
  return ["notstarted", "pendingentry", "revisiondue", "correctionrequired"].includes(status);
}

function employeeMatchesFilter(employee = {}, filter = {}) {
  return isSameOrBlank(employee.countryCode, filter.CountryCode) &&
    isSameOrBlank(employee.legalEntityCode, filter.LegalEntityCode) &&
    isSameOrBlank(employee.locationCode, filter.LocationCode) &&
    isSameOrBlank(employee.departmentCode, filter.DepartmentCode) &&
    isSameOrBlank(employee.workUnitCode, filter.WorkUnitCode) &&
    isSameOrBlank(employee.industryType, filter.IndustryType) &&
    isSameOrBlank(employee.employmentType, filter.EmploymentType) &&
    isSameOrBlank(employee.workArrangement, filter.WorkArrangement);
}
function getStructureMatchScore(structure = {}, employee = {}) {
  let score = 0;
  const checks = [
    [structure.countryCode, employee.countryCode, 16],
    [structure.legalEntityCode, employee.legalEntityCode, 14],
    [structure.locationCode, employee.locationCode, 10],
    [structure.departmentCode, employee.departmentCode, 8],
    [structure.workUnitCode, employee.workUnitCode, 8],
    [structure.industryType, employee.industryType, 12],
    [structure.employmentType, employee.employmentType, 12],
    [structure.workArrangement, employee.workArrangement, 6],
  ];

  for (const [structureValue, employeeValue, weight] of checks) {
    const s = normalizeMatch(structureValue);
    const e = normalizeMatch(employeeValue);
    if (!e) continue;
    if (s && s === e) score += weight;
    else if (!s || s === "all" || s === "global") score += Math.floor(weight / 2);
    else return -1;
  }
  return score;
}

function findBestCTCStructureForEmployee(employee = {}, allStructures = [], fallbackStructures = []) {
  const activeStructures = (allStructures || []).filter((item) => item && item.isActive !== false);
  const scored = activeStructures
    .map((structure) => ({ structure, score: getStructureMatchScore(structure, employee) }))
    .filter((item) => item.score >= 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length) return scored[0].structure;
  return (fallbackStructures || []).find((item) => item?.isActive !== false) || null;
}

function buildEmployeeMasterCTCPatch(employee = {}, currentDraft = {}, matchingStructure = null) {
  const revisionNumber = currentDraft.RevisionNumber || 1;
  const countryCode = employee.countryCode || currentDraft.CountryCode || "IN";
  return {
    EmployeeCTCId: currentDraft.EmployeeCTCId || buildEmployeeCTCId(employee.employeeCode, revisionNumber),
    EmployeeCode: employee.employeeCode || currentDraft.EmployeeCode || "",
    WorkEmail: employee.workEmail || currentDraft.WorkEmail || "",
    EmployeeName: employee.employeeName || currentDraft.EmployeeName || employee.employeeCode || "",
    CountryCode: countryCode,
    RegionGroup: employee.regionGroup || (countryCode === "DE" ? "EMEA" : "APAC"),
    LegalEntityCode: employee.legalEntityCode || currentDraft.LegalEntityCode || "",
    LocationCode: employee.locationCode || currentDraft.LocationCode || "",
    DepartmentCode: employee.departmentCode || currentDraft.DepartmentCode || "",
    WorkUnitCode: employee.workUnitCode || currentDraft.WorkUnitCode || "",
    IndustryType: employee.industryType || currentDraft.IndustryType || "Office / IT / Services",
    EmploymentType: employee.employmentType || currentDraft.EmploymentType || "Full Time",
    WorkArrangement: employee.workArrangement || currentDraft.WorkArrangement || "Office",
    FTEPercent: employee.ftePercent || currentDraft.FTEPercent || 100,
    PayGrade: employee.payGrade || currentDraft.PayGrade || "",
    SalaryCurrency: employee.salaryCurrency || matchingStructure?.defaultCurrency || currentDraft.SalaryCurrency || (countryCode === "DE" ? "EUR" : "INR"),
    CTCStructureId: matchingStructure?.ctcStructureId || currentDraft.CTCStructureId || "",
    PayFrequency: matchingStructure?.payFrequency || currentDraft.PayFrequency || "Annual",
  };
}


function getComponentValue(component = {}, businessName, fieldName, fallback = "") {
  const rawValue = getField(component.raw || {}, businessName, fieldName);
  const directValue = component[businessName];
  const camelValue = component[businessName?.charAt(0).toLowerCase() + businessName?.slice(1)];
  const value = rawValue !== undefined && rawValue !== null && rawValue !== "" ? rawValue
    : directValue !== undefined && directValue !== null && directValue !== "" ? directValue
    : camelValue !== undefined && camelValue !== null && camelValue !== "" ? camelValue
    : fallback;
  return displayText(value, fallback);
}

function getComponentBoolean(component = {}, businessName, fieldName, fallback = false) {
  const value = getField(component.raw || {}, businessName, fieldName);
  if (value === undefined || value === null || value === "") return component[businessName] ?? component[businessName?.charAt(0).toLowerCase() + businessName?.slice(1)] ?? fallback;
  return isTruthy(value);
}


function getApplicabilityType(component = {}) {
  const explicit = normalizeText(component.applicabilityType || component.ApplicabilityType || getComponentValue(component, "ApplicabilityType", "field_32", ""));
  if (explicit) return explicit;
  const category = normalizeCompare(component.componentCategory || getComponentValue(component, "ComponentCategory", "field_6"));
  const method = normalizeText(component.calculationMethod || getComponentValue(component, "CalculationMethod", "field_8"));
  if (getComponentBoolean(component, "IsStatutory", "field_15") || category === "statutory") return "Statutory";
  if (getComponentBoolean(component, "IsVariable", "field_19") || category === "variablepay" || category === "variable") return "Variable";
  if (method === "Derived") return "Balancing";
  if (category === "informational" || category === "information") return "Informational";
  return "Mandatory";
}
function isHRSelectableComponent(component = {}) {
  const explicit = component.hrSelectable ?? component.HRSelectable ?? getComponentValue(component, "HRSelectable", "field_33", "");
  if (explicit !== undefined && explicit !== null && explicit !== "") return isTruthy(explicit);
  return getApplicabilityType(component) === "Optional";
}
function getComponentCode(component = {}) {
  return normalizeText(component.componentCode || component.ComponentCode || getComponentValue(component, "ComponentCode", "field_3")).toUpperCase();
}
function shouldIncludeComponent(component = {}, selectedOptionalCodes = {}) {
  if (getApplicabilityType(component) !== "Optional") return true;
  return Boolean(selectedOptionalCodes[getComponentCode(component)]);
}
function groupComponentRows(rows = []) {
  return {
    mandatory: rows.filter((row) => row.applicabilityType === "Mandatory"),
    statutory: rows.filter((row) => row.applicabilityType === "Statutory" || row.isTaxComponent || row.isInsuranceOrStatutoryComponent),
    optional: rows.filter((row) => row.applicabilityType === "Optional"),
    variable: rows.filter((row) => row.applicabilityType === "Variable"),
    balancing: rows.filter((row) => row.applicabilityType === "Balancing"),
    informational: rows.filter((row) => row.applicabilityType === "Informational"),
  };
}
function buildDecisionPanelData(allComponents = [], includedComponents = [], draft = {}, manualOverrides = {}, selectedOptionalCodes = {}) {
  const budget = toNumber(draft.AnnualCTC, 0);
  const targetMonthly = toNumber(draft.TargetMonthlyNetPay, 0);
  const targetAnnual = targetMonthly * 12;
  const current = calculateComponentBreakup(includedComponents, draft, manualOverrides);
  const currentAnnualTakeHome = toNumber(current.totals.AnnualNetPayEstimate, 0);
  const shortfallAnnual = Math.max(targetAnnual - currentAnnualTakeHome, 0);
  const variableFlexiblePool = current.rows
    .filter((row) => row.applicabilityType === "Variable" && row.hrSelectable)
    .reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const canAdjustWithinBudget = Boolean(targetMonthly) && shortfallAnnual > 0 && shortfallAnnual <= variableFlexiblePool;
  const extraCTCRequired = targetMonthly ? Math.max(backCalculateAnnualCTCForTargetNet(includedComponents, draft, manualOverrides, targetMonthly) - budget, 0) : 0;
  let recommendation = "Enter Annual CTC and Target Monthly Take Home to view HR decision guidance.";
  if (targetMonthly && shortfallAnnual <= 0) recommendation = "Target take-home is achieved within the current CTC budget.";
  else if (targetMonthly && canAdjustWithinBudget) recommendation = `Target can be achieved within budget by reallocating approximately ${formatMoney(shortfallAnnual, draft.SalaryCurrency)} from HR-selectable variable/flexible pay to monthly cash/balancing allowance.`;
  else if (targetMonthly && shortfallAnnual > 0) recommendation = `Target needs extra company burden of about ${formatMoney(extraCTCRequired, draft.SalaryCurrency)} annually, unless HR adds/uses compliant optional components.`;
  return {
    budget,
    targetMonthly,
    currentTakeHomeAnnual: currentAnnualTakeHome,
    currentTakeHomeMonthly: current.totals.NetPayEstimateMonthly,
    shortfallAnnual: roundMoney(shortfallAnnual),
    shortfallMonthly: roundMoney(shortfallAnnual / 12),
    variableFlexiblePool: roundMoney(variableFlexiblePool),
    canAdjustWithinBudget,
    extraCTCRequired: roundMoney(extraCTCRequired),
    recommendation,
    suggestedNewComponents: ["Meal/Food Benefit", "Internet Allowance", "Mobile Reimbursement", "Vehicle/Conveyance Allowance", "LTA/Travel Benefit", "Medical/Wellness Benefit"],
  };
}

function getManualComponentCodes(components = []) {
  return (components || [])
    .filter((component) => normalizeText(component.calculationMethod || getComponentValue(component, "CalculationMethod", "field_8")) === "Manual")
    .map((component) => normalizeText(component.componentCode || getComponentValue(component, "ComponentCode", "field_3")).toUpperCase())
    .filter(Boolean);
}
function backCalculateAnnualCTCForTargetNet(components = [], draft = {}, manualOverrides = {}, targetMonthlyNetPay = 0) {
  const target = toNumber(targetMonthlyNetPay, 0);
  if (!target || target <= 0) return "";
  let low = 0;
  let high = Math.max(toNumber(draft.AnnualCTC, 0), target * 24, 100000);
  for (let i = 0; i < 20; i += 1) {
    const net = calculateComponentBreakup(components, { ...draft, AnnualCTC: high }, manualOverrides).totals.NetPayEstimateMonthly;
    if (net >= target) break;
    high *= 2;
  }
  for (let i = 0; i < 40; i += 1) {
    const mid = (low + high) / 2;
    const net = calculateComponentBreakup(components, { ...draft, AnnualCTC: mid }, manualOverrides).totals.NetPayEstimateMonthly;
    if (net < target) low = mid;
    else high = mid;
  }
  return Math.round(high);
}
function calculateComponentBreakup(components = [], draft = {}, manualOverrides = {}) {
  const annualCTC = toNumber(draft.AnnualCTC, 0);
  const annualGrossSeed = toNumber(draft.AnnualGross, annualCTC);
  const monthlyDivider = 12;
  const sorted = [...(components || [])].sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
  const amountsByCode = {};
  const rows = [];
  const normalizeBaseCodeForCalc = (value = "") => normalizeText(value).toUpperCase().replace(/[^A-Z0-9]/g, "");
  const getBaseAnnualAmount = (baseCode = "") => {
    const base = normalizeBaseCodeForCalc(baseCode);
    if (["ANNUALCTC", "CTC", "TOTALCTC", "PACKAGE"].includes(base)) return annualCTC;
    if (["ANNUALGROSS", "GROSS", "MONTHLYGROSS"].includes(base)) return annualGrossSeed;
    if (["BASIC", "BASICSALARY", "BASE"].includes(base)) return toNumber(amountsByCode.BASIC || amountsByCode.BASE, 0);
    return toNumber(amountsByCode[normalizeText(baseCode).toUpperCase()], 0);
  };
  const isCategory = (category, expected) => normalizeCompare(category) === normalizeCompare(expected);
  const looksLikeTax = (row) => {
    const haystack = `${row.componentCode || ""} ${row.componentName || ""} ${row.componentSubCategory || ""} ${row.displayGroup || ""}`.toLowerCase();
    return haystack.includes("tax") || haystack.includes("tds") || haystack.includes("wage tax") || haystack.includes("income tax") || haystack.includes("professional tax");
  };
  const looksLikeInsuranceOrStatutory = (row) => {
    const haystack = `${row.componentCode || ""} ${row.componentName || ""} ${row.componentSubCategory || ""} ${row.displayGroup || ""}`.toLowerCase();
    return haystack.includes("pf") || haystack.includes("epf") || haystack.includes("esi") || haystack.includes("pension") || haystack.includes("health") || haystack.includes("care") || haystack.includes("insurance") || haystack.includes("social") || haystack.includes("unemployment");
  };

  sorted.forEach((component) => {
    const code = normalizeText(component.componentCode || getComponentValue(component, "ComponentCode", "field_3")).toUpperCase();
    const category = normalizeText(component.componentCategory || getComponentValue(component, "ComponentCategory", "field_6"));
    const method = normalizeText(component.calculationMethod || getComponentValue(component, "CalculationMethod", "field_8"));
    const baseCode = normalizeText(getComponentValue(component, "CalculationBaseComponentCode", "field_9"));
    const percentage = toNumber(component.defaultPercentage || getComponentValue(component, "DefaultPercentage", "field_10"), 0);
    const fixedAmount = toNumber(component.defaultAmount || getComponentValue(component, "DefaultAmount", "field_11"), 0);
    const baseAmount = getBaseAnnualAmount(baseCode);
    const applicabilityType = getApplicabilityType(component);
    const hrSelectable = isHRSelectableComponent(component);
    const isManual = method === "Manual";
    const canEmployeeOverrideAmount = isManual || ((applicabilityType === "Variable" || applicabilityType === "Optional") && hrSelectable);
    let annualAmount = 0;

    if (canEmployeeOverrideAmount && manualOverrides[code] !== undefined) {
      annualAmount = toNumber(manualOverrides[code], 0);
    } else if (method === "FixedAmount") {
      annualAmount = fixedAmount !== 0 ? fixedAmount : (percentage > 0 && baseCode ? baseAmount * percentage / 100 : 0);
    } else if (method === "PercentageOfCTC") {
      annualAmount = annualCTC * percentage / 100;
    } else if (method === "PercentageOfGross") {
      annualAmount = annualGrossSeed * percentage / 100;
    } else if (method === "PercentageOfComponent" || method === "Percentage") {
      annualAmount = (baseCode ? baseAmount : annualCTC) * percentage / 100;
    } else if (method === "Derived") {
      annualAmount = 0;
    } else if (method === "NotCalculated" || method === "FormulaReference") {
      annualAmount = fixedAmount;
    } else {
      annualAmount = fixedAmount !== 0 ? fixedAmount : (percentage > 0 && baseCode ? baseAmount * percentage / 100 : 0);
    }

    annualAmount = roundMoney(annualAmount);
    amountsByCode[code] = annualAmount;

    const rowBase = {
      ctcComponentId: displayText(component.ctcComponentId || getComponentValue(component, "CTCComponentId", "field_1"), ""),
      ctcStructureId: displayText(component.ctcStructureId || getComponentValue(component, "CTCStructureId", "field_2"), ""),
      componentCode: code,
      componentName: displayText(component.componentName || getComponentValue(component, "ComponentName", "field_4")),
      componentCategory: category,
      componentSubCategory: displayText(component.componentSubCategory || getComponentValue(component, "ComponentSubCategory", "field_7")),
      calculationMethod: method,
      calculationBaseComponentCode: baseCode,
      defaultPercentage: percentage,
      annualAmount,
      monthlyAmount: roundMoney(annualAmount / monthlyDivider),
      isTaxable: getComponentValue(component, "IsTaxable", "field_14", "NotApplicable"),
      isStatutory: getComponentBoolean(component, "IsStatutory", "field_15") || isCategory(category, "Statutory"),
      isEmployerCost: getComponentBoolean(component, "IsEmployerCost", "field_16") || isCategory(category, "Employer Contribution"),
      isEmployeeDeduction: getComponentBoolean(component, "IsEmployeeDeduction", "field_17") || isCategory(category, "Deduction"),
      isCashComponent: getComponentBoolean(component, "IsCashComponent", "field_18", true),
      isVariable: getComponentBoolean(component, "IsVariable", "field_19") || isCategory(category, "Variable Pay") || isCategory(category, "Variable"),
      includeInAnnualCTC: getComponentBoolean(component, "IncludeInAnnualCTC", "field_22", true),
      includeInMonthlyGross: getComponentBoolean(component, "IncludeInMonthlyGross", "field_23", true),
      includeInNetPayEstimate: getComponentBoolean(component, "IncludeInNetPayEstimate", "field_24", true),
      displayGroup: getComponentValue(component, "DisplayGroup", "field_25", "Earnings"),
      displayOrder: component.displayOrder || getComponentValue(component, "DisplayOrder", "field_26", 999),
      applicabilityType,
      hrSelectable,
      manualOverrideAllowed: canEmployeeOverrideAmount,
    };
    rowBase.isTaxComponent = rowBase.isEmployeeDeduction && looksLikeTax(rowBase);
    rowBase.isInsuranceOrStatutoryComponent = rowBase.isEmployeeDeduction && !rowBase.isTaxComponent && (rowBase.isStatutory || looksLikeInsuranceOrStatutory(rowBase));
    rowBase.isPlainEmployeeDeduction = rowBase.isEmployeeDeduction && !rowBase.isTaxComponent && !rowBase.isInsuranceOrStatutoryComponent;
    rowBase.isFixedCashEarning = rowBase.isCashComponent && !rowBase.isEmployerCost && !rowBase.isEmployeeDeduction && !rowBase.isVariable;
    rowBase.isVariableTarget = rowBase.isVariable && !rowBase.isEmployeeDeduction && !rowBase.isEmployerCost;
    rows.push(rowBase);
  });

  const derivedRows = rows.filter((row) => row.calculationMethod === "Derived" && row.includeInAnnualCTC);
  if (derivedRows.length === 1 && annualCTC > 0) {
    const knownIncluded = rows.filter((row) => row.componentCode !== derivedRows[0].componentCode && row.includeInAnnualCTC).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
    const balance = Math.max(annualCTC - knownIncluded, 0);
    derivedRows[0].annualAmount = roundMoney(balance);
    derivedRows[0].monthlyAmount = roundMoney(balance / monthlyDivider);
  }

  const annualFixedGross = rows.filter((row) => row.isFixedCashEarning && row.includeInMonthlyGross).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualVariablePayTarget = rows.filter((row) => row.isVariableTarget).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualEmployerCost = rows.filter((row) => row.isEmployerCost).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualTaxDeductions = rows.filter((row) => row.isTaxComponent).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualStatutoryDeductions = rows.filter((row) => row.isInsuranceOrStatutoryComponent).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualOtherEmployeeDeductions = rows.filter((row) => row.isPlainEmployeeDeduction).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualEmployeeDeductions = annualTaxDeductions + annualStatutoryDeductions + annualOtherEmployeeDeductions;
  const annualGrossForNet = rows.filter((row) => row.includeInNetPayEstimate && !row.isEmployerCost && !row.isEmployeeDeduction && row.isCashComponent && !row.isVariableTarget).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualNetPayEstimate = Math.max(annualGrossForNet - annualEmployeeDeductions, 0);
  const annualGrossIncludingVariable = annualFixedGross + annualVariablePayTarget;
  const annualCompanyCost = annualFixedGross + annualVariablePayTarget + annualEmployerCost;
  const annualCTCComponentTotal = rows.filter((row) => row.includeInAnnualCTC).reduce((sum, row) => sum + toNumber(row.annualAmount), 0);
  const annualCTCDifference = annualCTC - annualCTCComponentTotal;

  return {
    rows,
    totals: {
      AnnualCTC: roundMoney(annualCTC),
      MonthlyCTC: roundMoney(annualCTC / monthlyDivider),
      AnnualGross: roundMoney(annualFixedGross || annualGrossSeed),
      MonthlyGross: roundMoney((annualFixedGross || annualGrossSeed) / monthlyDivider),
      AnnualEmployerCost: roundMoney(annualEmployerCost),
      MonthlyEmployerCost: roundMoney(annualEmployerCost / monthlyDivider),
      AnnualEmployeeDeductions: roundMoney(annualEmployeeDeductions),
      MonthlyEmployeeDeductions: roundMoney(annualEmployeeDeductions / monthlyDivider),
      AnnualTaxDeductions: roundMoney(annualTaxDeductions),
      AnnualStatutoryDeductions: roundMoney(annualStatutoryDeductions),
      AnnualOtherEmployeeDeductions: roundMoney(annualOtherEmployeeDeductions),
      AnnualVariablePayTarget: roundMoney(annualVariablePayTarget),
      AnnualGrossIncludingVariable: roundMoney(annualGrossIncludingVariable),
      AnnualNetPayEstimate: roundMoney(annualNetPayEstimate),
      NetPayEstimateMonthly: roundMoney(annualNetPayEstimate / monthlyDivider),
      AnnualCompanyCost: roundMoney(annualCompanyCost),
      AnnualCTCComponentTotal: roundMoney(annualCTCComponentTotal),
      AnnualCTCDifference: roundMoney(annualCTCDifference),
    },
    json: JSON.stringify(rows, null, 2),
  };
}
function cleanEmployeeCTCPayload(payload = {}) {
  const next = { ...payload };
  Object.keys(next).forEach((key) => {
    if (key === "ComponentBreakupJson") {
      next[key] = safeJsonString(next[key]);
      return;
    }
    if (next[key] && typeof next[key] === "object") {
      next[key] = normalizeText(next[key]);
    } else if (typeof next[key] === "string" && /"Value"\s*:/.test(next[key])) {
      next[key] = normalizeText(next[key]);
    }
  });
  return next;
}

function Compensation() {
  const [activeTab, setActiveTab] = useState("setup");
  const [hrMasterData, setHrMasterData] = useState({ countries: [], legalEntities: [], locations: [], departments: [], workUnits: [] });
  const [ctcMasterData, setCtcMasterData] = useState({ ctcStructures: [], ctcComponents: [] });
  const [componentCatalogue, setComponentCatalogue] = useState([]);
  const [payScaleBands, setPayScaleBands] = useState([]);
  const [payScaleForm, setPayScaleForm] = useState(EMPTY_PAY_SCALE_FORM);
  const [selectedPayScaleBandId, setSelectedPayScaleBandId] = useState("");
  const [payScaleEditorOpen, setPayScaleEditorOpen] = useState(false);
  const [payScaleEditorMode, setPayScaleEditorMode] = useState("add");
  const [catalogueFilterByIndustry, setCatalogueFilterByIndustry] = useState(false);
  const [catalogueFilterByEmploymentType, setCatalogueFilterByEmploymentType] = useState(false);
  const [orgFilter, setOrgFilter] = useState(EMPTY_ORG_FILTER);
  const [structureForm, setStructureForm] = useState(EMPTY_STRUCTURE_FORM);
  const [componentForm, setComponentForm] = useState(EMPTY_COMPONENT_FORM);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [employeeMasterRows, setEmployeeMasterRows] = useState([]);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState("");
  const [manualComponentOverrides, setManualComponentOverrides] = useState({});
  const [selectedOptionalComponentCodes, setSelectedOptionalComponentCodes] = useState({});
  const [employeeCode, setEmployeeCode] = useState("");
  const [currentCTC, setCurrentCTC] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [selectedPendingId, setSelectedPendingId] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [qualityData, setQualityData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [setupEditorOpen, setSetupEditorOpen] = useState(false);
  const [setupEditorType, setSetupEditorType] = useState("");
  const [setupEditorMode, setSetupEditorMode] = useState("add");
  const [selectedStructureId, setSelectedStructureId] = useState("");
  const [selectedComponentId, setSelectedComponentId] = useState("");
  const roleInfo = useRole();
  const currentUserRole = normalizeText(roleInfo.role || CURRENT_USER_ROLE);
  const currentUserEmail = normalizeText(roleInfo.email || CURRENT_USER_EMAIL);
  const currentRoleContext = useMemo(() => ({ email: currentUserEmail, role: currentUserRole }), [currentUserEmail, currentUserRole]);
  const canEditCTCSetup = Boolean(roleInfo.canManageCTCSetup) || canManageCTCSetup(currentUserRole);
  const canApproveCTC = Boolean(roleInfo.canApproveCTC) || canManageCTCSetup(currentUserRole);

  const countryOptions = useMemo(() => {
    return hrMasterData.countries.length ? hrMasterData.countries : COUNTRY_OPTIONS;
  }, [hrMasterData.countries]);

  const legalEntityOptions = useMemo(() => {
    return hrMasterData.legalEntities.filter((item) => !orgFilter.CountryCode || item.countryCode === orgFilter.CountryCode || item.countryCode === "ALL");
  }, [hrMasterData.legalEntities, orgFilter.CountryCode]);

  const locationOptions = useMemo(() => {
    return hrMasterData.locations.filter((item) => {
      const countryOk = !orgFilter.CountryCode || item.countryCode === orgFilter.CountryCode || item.countryCode === "ALL";
      const legalEntityOk = !orgFilter.LegalEntityCode || !item.legalEntityCode || item.legalEntityCode === orgFilter.LegalEntityCode;
      return countryOk && legalEntityOk;
    });
  }, [hrMasterData.locations, orgFilter.CountryCode, orgFilter.LegalEntityCode]);

  const departmentOptions = useMemo(() => {
    return hrMasterData.departments.filter((item) => !orgFilter.LegalEntityCode || !item.legalEntityCode || item.legalEntityCode === orgFilter.LegalEntityCode);
  }, [hrMasterData.departments, orgFilter.LegalEntityCode]);

  const workUnitOptions = useMemo(() => {
    return hrMasterData.workUnits.filter((item) => {
      const countryOk = !orgFilter.CountryCode || item.countryCode === orgFilter.CountryCode || item.countryCode === "ALL";
      const locationOk = !orgFilter.LocationCode || !item.locationCode || item.locationCode === orgFilter.LocationCode;
      const departmentOk = !orgFilter.DepartmentCode || !item.departmentCode || item.departmentCode === orgFilter.DepartmentCode;
      return countryOk && locationOk && departmentOk;
    });
  }, [hrMasterData.workUnits, orgFilter.CountryCode, orgFilter.LocationCode, orgFilter.DepartmentCode]);

  const normalizedEmployees = useMemo(() => {
    return (employeeMasterRows || []).map(normalizeEmployeeForCTC).filter((row) => row.employeeCode);
  }, [employeeMasterRows]);

  const eligibleEmployees = useMemo(() => {
    return normalizedEmployees
      .filter(isEmployeeEligibleForCTC)
      .filter((employee) => employeeMatchesFilter(employee, orgFilter));
  }, [normalizedEmployees, orgFilter]);

  const availableStructures = useMemo(() => {
    return (ctcMasterData.ctcStructures || []).filter((item) => {
      const countryOk = isSameOrBlank(item.countryCode, orgFilter.CountryCode);
      const legalEntityOk = isSameOrBlank(item.legalEntityCode, orgFilter.LegalEntityCode);
      const locationOk = isSameOrBlank(item.locationCode, orgFilter.LocationCode);
      const departmentOk = isSameOrBlank(item.departmentCode, orgFilter.DepartmentCode);
      const workUnitOk = isSameOrBlank(item.workUnitCode, orgFilter.WorkUnitCode);
      const industryOk = isSameOrBlank(item.industryType, orgFilter.IndustryType);
      const employmentOk = isSameOrBlank(item.employmentType, orgFilter.EmploymentType);
      const workArrangementOk = isSameOrBlank(item.workArrangement, orgFilter.WorkArrangement);
      return countryOk && legalEntityOk && locationOk && departmentOk && workUnitOk && industryOk && employmentOk && workArrangementOk && item.isActive !== false;
    });
  }, [ctcMasterData.ctcStructures, orgFilter.CountryCode, orgFilter.LegalEntityCode, orgFilter.LocationCode, orgFilter.DepartmentCode, orgFilter.WorkUnitCode, orgFilter.IndustryType, orgFilter.EmploymentType, orgFilter.WorkArrangement]);

  const selectedStructureComponents = useMemo(() => {
    const selectedStructureIdForComponents = selectedStructureId || componentForm.CTCStructureId || draft.CTCStructureId;
    const availableStructureIds = new Set((availableStructures || []).map((item) => item.ctcStructureId).filter(Boolean));

    return (ctcMasterData.ctcComponents || [])
      .filter((item) => {
        if (selectedStructureIdForComponents) {
          return item.ctcStructureId === selectedStructureIdForComponents && item.isActive !== false;
        }

        // When no structure is selected, do not show all components globally.
        // Show only components that belong to currently available structures for the selected country/industry/employment filters.
        return availableStructureIds.has(item.ctcStructureId) &&
          isSameOrBlank(item.countryCode, orgFilter.CountryCode) &&
          item.isActive !== false;
      })
      .sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0));
  }, [ctcMasterData.ctcComponents, availableStructures, orgFilter.CountryCode, selectedStructureId, componentForm.CTCStructureId, draft.CTCStructureId]);

  const includedStructureComponents = useMemo(() => {
    return selectedStructureComponents.filter((component) => shouldIncludeComponent(component, selectedOptionalComponentCodes));
  }, [selectedStructureComponents, selectedOptionalComponentCodes]);

  const availableOptionalComponents = useMemo(() => {
    return selectedStructureComponents.filter((component) => getApplicabilityType(component) === "Optional" && isHRSelectableComponent(component));
  }, [selectedStructureComponents]);


  const filteredComponentCatalogue = useMemo(() => {
    return (componentCatalogue || []).filter((item) => {
      if (item.isActive === false) return false;
      const countryOk = isSameOrBlank(item.countryCode, orgFilter.CountryCode);
      const industryOk = !catalogueFilterByIndustry || listMatchesSelection(item.applicableIndustryTypes, orgFilter.IndustryType);
      const employmentOk = !catalogueFilterByEmploymentType || listMatchesSelection(item.applicableEmploymentTypes, orgFilter.EmploymentType);
      return countryOk && industryOk && employmentOk;
    });
  }, [componentCatalogue, orgFilter.CountryCode, orgFilter.IndustryType, orgFilter.EmploymentType, catalogueFilterByIndustry, catalogueFilterByEmploymentType]);
  const filteredPayScaleBands = useMemo(() => {
    return (payScaleBands || []).filter((band) => {
      if (band.isActive === false) return false;
      return isSameOrBlank(band.countryCode, orgFilter.CountryCode) &&
        isSameOrBlank(band.legalEntityCode, orgFilter.LegalEntityCode) &&
        isSameOrBlank(band.industryType, orgFilter.IndustryType) &&
        isSameOrBlank(band.employmentType, orgFilter.EmploymentType);
    }).sort((a, b) => Number(a.displayOrder || 999) - Number(b.displayOrder || 999));
  }, [payScaleBands, orgFilter.CountryCode, orgFilter.LegalEntityCode, orgFilter.IndustryType, orgFilter.EmploymentType]);

  const selectedPayScaleBand = useMemo(() => {
    return (payScaleBands || []).find((band) => String(band.id || band.ID || band.payGradeCode) === String(selectedPayScaleBandId)) || null;
  }, [payScaleBands, selectedPayScaleBandId]);

  const catalogueGroups = useMemo(() => ({
    mandatory: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Mandatory"),
    statutory: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Statutory"),
    variable: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Variable"),
    optional: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Optional"),
    balancing: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Balancing"),
    informational: filteredComponentCatalogue.filter((row) => row.applicabilityType === "Informational"),
  }), [filteredComponentCatalogue]);

  const selectedPending = useMemo(() => {
    return pendingApprovals.find((row) => row.employeeCTCId === selectedPendingId) || null;
  }, [pendingApprovals, selectedPendingId]);

  const selectedPendingBreakupRows = useMemo(() => {
    if (!selectedPending) return [];
    const rawJson = selectedPending.componentBreakupJson || getField(selectedPending.raw || {}, "ComponentBreakupJson", "field_31") || "[]";
    try {
      const parsed = typeof rawJson === "string" ? JSON.parse(rawJson || "[]") : rawJson;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [selectedPending]);

  const selectedPendingGroups = useMemo(() => groupComponentRows(selectedPendingBreakupRows), [selectedPendingBreakupRows]);

  const selectedPendingStructure = useMemo(() => {
    if (!selectedPending?.ctcStructureId) return null;
    return (ctcMasterData.ctcStructures || []).find((row) => row.ctcStructureId === selectedPending.ctcStructureId) || null;
  }, [ctcMasterData.ctcStructures, selectedPending]);

  const selectedPendingTotals = useMemo(() => {
    const rows = selectedPendingBreakupRows || [];
    const annualCTC = toNumber(selectedPending?.annualCTC, 0);
    const annualGross = toNumber(selectedPending?.annualGross, 0);
    const annualEmployerCost = toNumber(selectedPending?.annualEmployerCost, 0);
    const annualEmployeeDeductions = toNumber(selectedPending?.annualEmployeeDeductions, 0);
    const annualVariablePayTarget = toNumber(selectedPending?.annualVariablePayTarget, 0);
    const annualNetPayEstimate = rows
      .filter((row) => row.includeInNetPayEstimate && !row.isEmployerCost && !row.isEmployeeDeduction && row.isCashComponent && !row.isVariableTarget)
      .reduce((sum, row) => sum + toNumber(row.annualAmount), 0) - annualEmployeeDeductions;
    return {
      annualCTC,
      monthlyCTC: toNumber(selectedPending?.monthlyCTC, annualCTC / 12),
      annualGross,
      monthlyGross: toNumber(selectedPending?.monthlyGross, annualGross / 12),
      annualEmployerCost,
      annualEmployeeDeductions,
      annualVariablePayTarget,
      netPayMonthly: toNumber(selectedPending?.netPayEstimateMonthly, Math.max(annualNetPayEstimate, 0) / 12),
    };
  }, [selectedPending, selectedPendingBreakupRows]);


  const selectedStructure = useMemo(() => {
    return (ctcMasterData.ctcStructures || []).find((row) => row.ctcStructureId === selectedStructureId) || null;
  }, [ctcMasterData.ctcStructures, selectedStructureId]);

  const selectedComponent = useMemo(() => {
    return (ctcMasterData.ctcComponents || []).find((row) => row.ctcComponentId === selectedComponentId) || null;
  }, [ctcMasterData.ctcComponents, selectedComponentId]);

  const calculatedBreakup = useMemo(() => {
    return calculateComponentBreakup(includedStructureComponents, draft, manualComponentOverrides);
  }, [includedStructureComponents, draft.AnnualCTC, draft.AnnualGross, manualComponentOverrides]);

  const componentGroups = useMemo(() => groupComponentRows(calculatedBreakup.rows), [calculatedBreakup.rows]);

  const decisionPanel = useMemo(() => {
    return buildDecisionPanelData(selectedStructureComponents, includedStructureComponents, draft, manualComponentOverrides, selectedOptionalComponentCodes);
  }, [selectedStructureComponents, includedStructureComponents, draft.AnnualCTC, draft.TargetMonthlyNetPay, draft.SalaryCurrency, manualComponentOverrides, selectedOptionalComponentCodes]);

  const manualComponentCodes = useMemo(() => getManualComponentCodes(includedStructureComponents), [includedStructureComponents]);

  const showMessage = (text, type = "success") => {
    setMessage(text || "");
    setMessageType(type);
  };

  const refreshCTCMasterData = async (filter = orgFilter) => {
    const refreshed = await loadCTCMasterData({
      countryCode: filter.CountryCode || "",
      legalEntityCode: filter.LegalEntityCode || "",
      locationCode: filter.LocationCode || "",
      departmentCode: filter.DepartmentCode || "",
      workUnitCode: filter.WorkUnitCode || "",
      industryType: filter.IndustryType || "",
      employmentType: filter.EmploymentType || "",
      workArrangement: filter.WorkArrangement || "",
      includeInactive: false,
    }, buildContext(currentRoleContext));
    setCtcMasterData(refreshed);
    return refreshed;
  };

  const refreshComponentCatalogue = async (filter = orgFilter) => {
    const rows = await getComponentCatalogue({
      countryCode: filter.CountryCode || "",
      includeInactive: false,
    }, buildContext(currentRoleContext));
    setComponentCatalogue(rows || []);
    return rows || [];
  };

  const refreshPayScaleBands = async (filter = orgFilter) => {
    const rows = await getPayScaleBands({
      countryCode: filter.CountryCode || "",
      legalEntityCode: filter.LegalEntityCode || "",
      industryType: filter.IndustryType || "",
      employmentType: filter.EmploymentType || "",
      includeInactive: false,
    }, buildContext(currentRoleContext));
    setPayScaleBands(rows || []);
    return rows || [];
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      showMessage("");

      const [hrData, ctcData, catalogueRows, payScaleRows, pending, employees] = await Promise.all([
        getHRSettingsMasterData(),
        loadCTCMasterData({
          countryCode: orgFilter.CountryCode || "",
          legalEntityCode: orgFilter.LegalEntityCode || "",
          locationCode: orgFilter.LocationCode || "",
          departmentCode: orgFilter.DepartmentCode || "",
          workUnitCode: orgFilter.WorkUnitCode || "",
          industryType: orgFilter.IndustryType || "",
          employmentType: orgFilter.EmploymentType || "",
          workArrangement: orgFilter.WorkArrangement || "",
          includeInactive: false,
        }, buildContext(currentRoleContext)),
        getComponentCatalogue({
          countryCode: orgFilter.CountryCode || "",
          includeInactive: false,
        }, buildContext(currentRoleContext)),
        getPayScaleBands({
          countryCode: orgFilter.CountryCode || "",
          legalEntityCode: orgFilter.LegalEntityCode || "",
          industryType: orgFilter.IndustryType || "",
          employmentType: orgFilter.EmploymentType || "",
          includeInactive: false,
        }, buildContext(currentRoleContext)).catch((error) => {
          console.warn("Pay scale bands load skipped:", error);
          return [];
        }),
        getPendingCTCApprovals({}, buildContext(currentRoleContext)),
        getEmployeeMasters(),
      ]);

      const normalizedHR = normalizeHRMasterData(hrData);
      const selectedEntity = normalizedHR.legalEntities.find((item) => item.value === orgFilter.LegalEntityCode);

      setHrMasterData(normalizedHR);
      setCtcMasterData(ctcData);
      setComponentCatalogue(catalogueRows || []);
      setPayScaleBands(payScaleRows || []);
      setPendingApprovals(pending);
      setEmployeeMasterRows(employees || []);

      // Refresh clears stale form values and regenerates IDs for current filters.
      setStructureForm(createStructureFormForFilter(orgFilter, ctcData.ctcStructures || [], selectedEntity));
      setComponentForm(createComponentFormForFilter(orgFilter, "", ctcData.ctcComponents || []));
      setDraft(createDraftForFilter(orgFilter, selectedEntity));
      setSelectedStructureId("");
      setSelectedComponentId("");
      setSelectedEmployeeCode("");
      setManualComponentOverrides({});
      setSelectedOptionalComponentCodes({});
      setSelectedPendingId("");
      setRejectionReason("");
      setApprovalRemarks("");
      setQualityData(null);
    } catch (error) {
      console.error("CTC initial load error:", error);
      showMessage(error.message || "Failed to load CTC data.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyOrgFilterToForms = (nextFilter, latestCTCData = ctcMasterData) => {
    const selectedEntity = hrMasterData.legalEntities.find((item) => item.value === nextFilter.LegalEntityCode);

    // Country/industry/employment changes must not reuse old CTC StructureId or ComponentId.
    setStructureForm(createStructureFormForFilter(nextFilter, latestCTCData.ctcStructures || [], selectedEntity));
    setComponentForm(createComponentFormForFilter(nextFilter, "", latestCTCData.ctcComponents || []));
    setDraft(createDraftForFilter(nextFilter, selectedEntity));
    setSelectedStructureId("");
    setSelectedComponentId("");
    setSelectedEmployeeCode("");
    setManualComponentOverrides({});
  };

  const handleOrgFilterChange = async (event) => {
    const { name, value } = event.target;
    const next = buildNextOrgFilter(orgFilter, name, value);

    setOrgFilter(next);

    try {
      let latestCTCData = ctcMasterData;
      if (["CountryCode", "LegalEntityCode", "LocationCode", "DepartmentCode", "WorkUnitCode", "IndustryType", "EmploymentType", "WorkArrangement"].includes(name)) {
        latestCTCData = await refreshCTCMasterData(next);
      }
      if (name === "CountryCode") {
        await refreshComponentCatalogue(next);
      }
      if (["CountryCode", "LegalEntityCode", "IndustryType", "EmploymentType"].includes(name)) {
        await refreshPayScaleBands(next).catch((error) => console.warn("Pay scale refresh skipped:", error));
      }
      applyOrgFilterToForms(next, latestCTCData);
    } catch (error) {
      console.error("CTC filter refresh error:", error);
      showMessage(error.message || "Failed to refresh CTC structures for selected filters.", "error");
      applyOrgFilterToForms(next, ctcMasterData);
    }
  };

  const handleStructureChange = (event) => {
    const { name, value, type, checked } = event.target;
    setStructureForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "EffectiveFrom" && value && !prev.EffectiveTo) {
        next.EffectiveTo = addDaysToDateInput(value, 365);
      }
      if (["CountryCode", "IndustryType", "EmploymentType", "WorkArrangement"].includes(name)) {
        next.CTCStructureId = buildSuggestedStructureId(next, ctcMasterData.ctcStructures || []);
        next.RegionGroup = next.CountryCode === "DE" ? "EMEA" : "APAC";
        next.DefaultCurrency = next.CountryCode === "DE" ? "EUR" : "INR";
      }
      return next;
    });
  };

  const handleComponentChange = (event) => {
    const { name, value, type, checked } = event.target;
    setComponentForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "CountryCode") {
        next.CTCStructureId = "";
        next.CTCComponentId = "";
        next.ComponentCode = "";
        next.ComponentName = "";
      }

      if (name === "ComponentCategory") {
        const groupMap = {
          Earning: "Earnings",
          Allowance: "Earnings",
          Deduction: "Employee Deductions",
          "Employer Contribution": "Employer Contributions",
          Variable: "Variable Pay",
          "Variable Pay": "Variable Pay",
          Benefit: "Benefits",
          Information: "Informational",
          Informational: "Informational",
          Statutory: "Employer Contributions",
        };
        next.DisplayGroup = groupMap[value] || next.DisplayGroup || "Earnings";
        if (!next.ComponentCode && next.CTCStructureId) {
          next.ComponentCode = getSuggestedComponentCode(value, ctcMasterData.ctcComponents || [], next.CTCStructureId);
        }
      }

      if (["ComponentCode", "CTCStructureId", "ComponentCategory"].includes(name)) {
        next.CTCComponentId = next.CTCStructureId && next.ComponentCode
          ? buildSuggestedComponentId(next.CTCStructureId, next.ComponentCode)
          : "";
      }

      return next;
    });
  };

  const syncDraftTotals = (baseDraft, breakup = calculatedBreakup) => {
    return {
      ...baseDraft,
      MonthlyCTC: breakup.totals.MonthlyCTC || "",
      AnnualGross: breakup.totals.AnnualGross || "",
      MonthlyGross: breakup.totals.MonthlyGross || "",
      AnnualEmployerCost: breakup.totals.AnnualEmployerCost || "",
      MonthlyEmployerCost: breakup.totals.MonthlyEmployerCost || "",
      AnnualEmployeeDeductions: breakup.totals.AnnualEmployeeDeductions || "",
      MonthlyEmployeeDeductions: breakup.totals.MonthlyEmployeeDeductions || "",
      AnnualVariablePayTarget: breakup.totals.AnnualVariablePayTarget || "",
      NetPayEstimateAvailable: breakup.totals.NetPayEstimateMonthly !== 0,
      NetPayEstimateMonthly: breakup.totals.NetPayEstimateMonthly || "",
      ComponentBreakupJson: breakup.json,
    };
  };

  const applyEmployeeCTCDraftToForm = (draftRow = {}, sourceLabel = "draft") => {
    if (!draftRow) return false;
    const raw = draftRow.raw || draftRow;
    const employeeCodeValue = draftRow.employeeCode || getField(raw, "EmployeeCode", "field_2") || draft.EmployeeCode || employeeCode;
    const structureIdValue = draftRow.ctcStructureId || getField(raw, "CTCStructureId", "field_16") || draft.CTCStructureId;
    const rawBreakup = draftRow.componentBreakupJson || getField(raw, "ComponentBreakupJson", "field_31") || "[]";
    let breakupRows = [];
    try {
      breakupRows = typeof rawBreakup === "string" ? JSON.parse(rawBreakup || "[]") : rawBreakup;
      if (!Array.isArray(breakupRows)) breakupRows = [];
    } catch {
      breakupRows = [];
    }
    const restoredOptionalCodes = {};
    const restoredOverrides = {};
    breakupRows.forEach((row) => {
      const code = normalizeText(row.componentCode || row.ComponentCode).toUpperCase();
      if (!code) return;
      const applicabilityType = normalizeText(row.applicabilityType || row.ApplicabilityType);
      const hrSelectable = isTruthy(row.hrSelectable ?? row.HRSelectable);
      if (applicabilityType === "Optional") restoredOptionalCodes[code] = true;
      if (row.manualOverrideAllowed || row.ManualOverrideAllowed || ((applicabilityType === "Variable" || applicabilityType === "Optional") && hrSelectable)) {
        restoredOverrides[code] = toNumber(row.annualAmount ?? row.AnnualAmount, 0);
      }
    });
    if (structureIdValue) {
      setSelectedStructureId(structureIdValue);
      setComponentForm((prev) => ({ ...prev, CTCStructureId: structureIdValue }));
    }
    setSelectedOptionalComponentCodes(restoredOptionalCodes);
    setManualComponentOverrides(restoredOverrides);
    setEmployeeCode(employeeCodeValue || "");
    setSelectedEmployeeCode(employeeCodeValue || "");
    setDraft((prev) => ({
      ...prev,
      EmployeeCTCId: draftRow.employeeCTCId || getField(raw, "EmployeeCTCId", "field_1") || prev.EmployeeCTCId,
      EmployeeCode: employeeCodeValue || prev.EmployeeCode,
      WorkEmail: draftRow.workEmail || getField(raw, "WorkEmail", "field_3") || prev.WorkEmail,
      EmployeeName: draftRow.employeeName || getField(raw, "EmployeeName", "field_4") || prev.EmployeeName,
      CountryCode: draftRow.countryCode || getField(raw, "CountryCode", "field_5") || prev.CountryCode,
      RegionGroup: draftRow.regionGroup || getField(raw, "RegionGroup", "field_6") || prev.RegionGroup,
      LegalEntityCode: draftRow.legalEntityCode || getField(raw, "LegalEntityCode", "field_7") || prev.LegalEntityCode,
      LocationCode: draftRow.locationCode || getField(raw, "LocationCode", "field_8") || prev.LocationCode,
      DepartmentCode: draftRow.departmentCode || getField(raw, "DepartmentCode", "field_9") || prev.DepartmentCode,
      WorkUnitCode: draftRow.workUnitCode || getField(raw, "WorkUnitCode", "field_10") || prev.WorkUnitCode,
      IndustryType: draftRow.industryType || getField(raw, "IndustryType", "field_11") || prev.IndustryType,
      EmploymentType: draftRow.employmentType || getField(raw, "EmploymentType", "field_12") || prev.EmploymentType,
      WorkArrangement: draftRow.workArrangement || getField(raw, "WorkArrangement", "field_13") || prev.WorkArrangement,
      FTEPercent: draftRow.ftePercent || getField(raw, "FTEPercent", "field_14") || prev.FTEPercent,
      PayGrade: draftRow.payGrade || getField(raw, "PayGrade", "field_15") || prev.PayGrade,
      CTCStructureId: structureIdValue || prev.CTCStructureId,
      PayFrequency: draftRow.payFrequency || getField(raw, "PayFrequency", "field_17") || prev.PayFrequency,
      SalaryCurrency: draftRow.salaryCurrency || getField(raw, "SalaryCurrency", "field_18") || prev.SalaryCurrency,
      AnnualCTC: draftRow.annualCTC || getField(raw, "AnnualCTC", "field_19") || prev.AnnualCTC,
      MonthlyCTC: draftRow.monthlyCTC || getField(raw, "MonthlyCTC", "field_20") || prev.MonthlyCTC,
      AnnualGross: draftRow.annualGross || getField(raw, "AnnualGross", "field_21") || prev.AnnualGross,
      MonthlyGross: draftRow.monthlyGross || getField(raw, "MonthlyGross", "field_22") || prev.MonthlyGross,
      AnnualEmployerCost: draftRow.annualEmployerCost || getField(raw, "AnnualEmployerCost", "field_23") || prev.AnnualEmployerCost,
      MonthlyEmployerCost: draftRow.monthlyEmployerCost || getField(raw, "MonthlyEmployerCost", "field_24") || prev.MonthlyEmployerCost,
      AnnualEmployeeDeductions: draftRow.annualEmployeeDeductions || getField(raw, "AnnualEmployeeDeductions", "field_25") || prev.AnnualEmployeeDeductions,
      MonthlyEmployeeDeductions: draftRow.monthlyEmployeeDeductions || getField(raw, "MonthlyEmployeeDeductions", "field_26") || prev.MonthlyEmployeeDeductions,
      AnnualVariablePayTarget: draftRow.annualVariablePayTarget || getField(raw, "AnnualVariablePayTarget", "field_27") || prev.AnnualVariablePayTarget,
      HourlyRate: draftRow.hourlyRate || getField(raw, "HourlyRate", "field_28") || prev.HourlyRate,
      NetPayEstimateAvailable: draftRow.netPayEstimateAvailable ?? isTruthy(getField(raw, "NetPayEstimateAvailable", "field_29")) ?? prev.NetPayEstimateAvailable,
      NetPayEstimateMonthly: draftRow.netPayEstimateMonthly || getField(raw, "NetPayEstimateMonthly", "field_30") || prev.NetPayEstimateMonthly,
      ComponentBreakupJson: safeJsonString(rawBreakup),
      EffectiveFrom: draftRow.effectiveFrom || getField(raw, "EffectiveFrom", "field_32") || prev.EffectiveFrom,
      EffectiveTo: draftRow.effectiveTo || getField(raw, "EffectiveTo", "field_33") || prev.EffectiveTo,
      RevisionNumber: draftRow.revisionNumber || getField(raw, "RevisionNumber", "field_45") || prev.RevisionNumber,
      RevisionReason: draftRow.revisionReason || getField(raw, "RevisionReason", "field_46") || prev.RevisionReason,
      PreviousEmployeeCTCId: draftRow.previousEmployeeCTCId || getField(raw, "PreviousEmployeeCTCId", "field_47") || prev.PreviousEmployeeCTCId,
      ConfidentialityLevel: draftRow.confidentialityLevel || getField(raw, "ConfidentialityLevel", "field_49") || prev.ConfidentialityLevel,
      Remarks: draftRow.remarks || getField(raw, "Remarks", "field_50") || prev.Remarks,
    }));
    showMessage(`Loaded ${sourceLabel} for ${employeeCodeValue || "selected employee"}.`, "success");
    return true;
  };

  const applyCurrentCTCRevisionToForm = (currentRow = {}, selectedEmployee = null, sourceLabel = "current approved CTC") => {
    if (!currentRow) return false;
    const raw = currentRow.raw || currentRow;
    const pick = (...names) => {
      for (const name of names) {
        if (currentRow?.[name] !== undefined && currentRow?.[name] !== null && currentRow?.[name] !== "") return currentRow[name];
      }
      return getField(raw, ...names);
    };
    const employeeCodeValue = normalizeText(pick("employeeCode", "EmployeeCode", "field_2") || selectedEmployee?.employeeCode || draft.EmployeeCode || employeeCode);
    const currentEmployeeCTCId = normalizeText(pick("employeeCTCId", "EmployeeCTCId", "field_1"));
    const structureIdValue = normalizeText(pick("ctcStructureId", "CTCStructureId", "field_16"));
    const currentRevision = toNumber(pick("revisionNumber", "RevisionNumber", "field_45"), 0);
    const nextRevision = Math.max(currentRevision + 1, toNumber(draft.RevisionNumber, 1));
    const rawBreakup = pick("componentBreakupJson", "ComponentBreakupJson", "field_31") || "[]";
    let breakupRows = [];
    try {
      breakupRows = typeof rawBreakup === "string" ? JSON.parse(rawBreakup || "[]") : rawBreakup;
      if (!Array.isArray(breakupRows)) breakupRows = [];
    } catch { breakupRows = []; }
    const restoredOptionalCodes = {};
    const restoredOverrides = {};
    breakupRows.forEach((row) => {
      const code = normalizeText(row.componentCode || row.ComponentCode).toUpperCase();
      if (!code) return;
      const applicabilityType = normalizeText(row.applicabilityType || row.ApplicabilityType);
      const hrSelectable = isTruthy(row.hrSelectable ?? row.HRSelectable);
      if (applicabilityType === "Optional") restoredOptionalCodes[code] = true;
      if (row.manualOverrideAllowed || row.ManualOverrideAllowed || ((applicabilityType === "Variable" || applicabilityType === "Optional") && hrSelectable)) {
        restoredOverrides[code] = toNumber(row.annualAmount ?? row.AnnualAmount, 0);
      }
    });
    if (structureIdValue) {
      setSelectedStructureId(structureIdValue);
      setComponentForm((prev) => ({ ...prev, CTCStructureId: structureIdValue }));
    }
    setSelectedOptionalComponentCodes(restoredOptionalCodes);
    setManualComponentOverrides(restoredOverrides);
    setEmployeeCode(employeeCodeValue || "");
    setSelectedEmployeeCode(employeeCodeValue || "");
    const nextDraft = ensureEffectiveToFromEffectiveFrom({
      ...draft,
      EmployeeCTCId: buildEmployeeCTCId(employeeCodeValue, nextRevision),
      EmployeeCode: employeeCodeValue,
      WorkEmail: normalizeText(pick("workEmail", "WorkEmail", "field_3") || selectedEmployee?.workEmail || draft.WorkEmail),
      EmployeeName: normalizeText(pick("employeeName", "EmployeeName", "field_4") || selectedEmployee?.employeeName || draft.EmployeeName),
      CountryCode: normalizeText(pick("countryCode", "CountryCode", "field_5") || selectedEmployee?.countryCode || draft.CountryCode),
      RegionGroup: normalizeText(pick("regionGroup", "RegionGroup", "field_6") || selectedEmployee?.regionGroup || draft.RegionGroup),
      LegalEntityCode: normalizeText(pick("legalEntityCode", "LegalEntityCode", "field_7") || selectedEmployee?.legalEntityCode || draft.LegalEntityCode),
      LocationCode: normalizeText(pick("locationCode", "LocationCode", "field_8") || selectedEmployee?.locationCode || draft.LocationCode),
      DepartmentCode: normalizeText(pick("departmentCode", "DepartmentCode", "field_9") || selectedEmployee?.departmentCode || draft.DepartmentCode),
      WorkUnitCode: normalizeText(pick("workUnitCode", "WorkUnitCode", "field_10") || selectedEmployee?.workUnitCode || draft.WorkUnitCode),
      IndustryType: normalizeText(pick("industryType", "IndustryType", "field_11") || selectedEmployee?.industryType || draft.IndustryType),
      EmploymentType: normalizeText(pick("employmentType", "EmploymentType", "field_12") || selectedEmployee?.employmentType || draft.EmploymentType),
      WorkArrangement: normalizeText(pick("workArrangement", "WorkArrangement", "field_13") || selectedEmployee?.workArrangement || draft.WorkArrangement),
      FTEPercent: toNumber(pick("ftePercent", "FTEPercent", "field_14") || selectedEmployee?.ftePercent, draft.FTEPercent || 100),
      PayGrade: normalizeText(pick("payGrade", "PayGrade", "field_15") || selectedEmployee?.payGrade || draft.PayGrade),
      CTCStructureId: structureIdValue || draft.CTCStructureId,
      PayFrequency: normalizeText(pick("payFrequency", "PayFrequency", "field_17") || draft.PayFrequency),
      SalaryCurrency: normalizeText(pick("salaryCurrency", "SalaryCurrency", "field_18") || selectedEmployee?.salaryCurrency || draft.SalaryCurrency),
      AnnualCTC: toNumber(pick("annualCTC", "AnnualCTC", "field_19"), draft.AnnualCTC || ""),
      MonthlyCTC: toNumber(pick("monthlyCTC", "MonthlyCTC", "field_20"), draft.MonthlyCTC || ""),
      AnnualGross: toNumber(pick("annualGross", "AnnualGross", "field_21"), draft.AnnualGross || ""),
      MonthlyGross: toNumber(pick("monthlyGross", "MonthlyGross", "field_22"), draft.MonthlyGross || ""),
      AnnualEmployerCost: toNumber(pick("annualEmployerCost", "AnnualEmployerCost", "field_23"), draft.AnnualEmployerCost || ""),
      MonthlyEmployerCost: toNumber(pick("monthlyEmployerCost", "MonthlyEmployerCost", "field_24"), draft.MonthlyEmployerCost || ""),
      AnnualEmployeeDeductions: toNumber(pick("annualEmployeeDeductions", "AnnualEmployeeDeductions", "field_25"), draft.AnnualEmployeeDeductions || ""),
      MonthlyEmployeeDeductions: toNumber(pick("monthlyEmployeeDeductions", "MonthlyEmployeeDeductions", "field_26"), draft.MonthlyEmployeeDeductions || ""),
      AnnualVariablePayTarget: toNumber(pick("annualVariablePayTarget", "AnnualVariablePayTarget", "field_27"), draft.AnnualVariablePayTarget || ""),
      HourlyRate: toNumber(pick("hourlyRate", "HourlyRate", "field_28"), draft.HourlyRate || ""),
      NetPayEstimateAvailable: isTruthy(pick("netPayEstimateAvailable", "NetPayEstimateAvailable", "field_29")) || draft.NetPayEstimateAvailable,
      NetPayEstimateMonthly: toNumber(pick("netPayEstimateMonthly", "NetPayEstimateMonthly", "field_30"), draft.NetPayEstimateMonthly || ""),
      TargetMonthlyNetPay: draft.TargetMonthlyNetPay || "",
      ComponentBreakupJson: safeJsonString(rawBreakup),
      EffectiveFrom: normalizeText(pick("effectiveFrom", "EffectiveFrom", "field_32") || draft.EffectiveFrom),
      EffectiveTo: normalizeText(pick("effectiveTo", "EffectiveTo", "field_33") || draft.EffectiveTo),
      RevisionNumber: nextRevision,
      RevisionReason: "",
      PreviousEmployeeCTCId: currentEmployeeCTCId || draft.PreviousEmployeeCTCId,
      ConfidentialityLevel: normalizeText(pick("confidentialityLevel", "ConfidentialityLevel", "field_49") || draft.ConfidentialityLevel || "HRConfidential"),
      Remarks: "",
    });
    const structureComponents = structureIdValue
      ? (ctcMasterData.ctcComponents || []).filter((item) => item.ctcStructureId === structureIdValue && shouldIncludeComponent(item, restoredOptionalCodes))
      : includedStructureComponents;
    const latest = calculateComponentBreakup(structureComponents, nextDraft, restoredOverrides);
    setDraft(syncDraftTotals(nextDraft, latest));
    showMessage(`Loaded ${sourceLabel} for ${employeeCodeValue || "selected employee"}. Revision ${nextRevision} draft is ready for HR changes.`, "success");
    return true;
  };

  const handleSelectEligibleEmployee = async (employee) => {
    setSelectedEmployeeCode(employee.employeeCode);
    setEmployeeCode(employee.employeeCode);
    setManualComponentOverrides({});
    setSelectedOptionalComponentCodes({});

    const matchingStructure = findBestCTCStructureForEmployee(
      employee,
      ctcMasterData.ctcStructures || [],
      availableStructures
    );

    const nextRevisionNumber = draft.RevisionNumber || 1;
    const employeeMasterPatch = buildEmployeeMasterCTCPatch(
      employee,
      { ...draft, RevisionNumber: nextRevisionNumber },
      matchingStructure
    );

    const nextDraft = ensureEffectiveToFromEffectiveFrom({
      ...draft,
      ...employeeMasterPatch,
    });

    if (matchingStructure?.ctcStructureId) {
      setSelectedStructureId(matchingStructure.ctcStructureId);
      setComponentForm((prev) => ({
        ...prev,
        CTCStructureId: matchingStructure.ctcStructureId,
        CountryCode: employee.countryCode || orgFilter.CountryCode,
      }));
    }

    const structureComponents = matchingStructure?.ctcStructureId
      ? (ctcMasterData.ctcComponents || []).filter((item) => item.ctcStructureId === matchingStructure.ctcStructureId && shouldIncludeComponent(item, {}))
      : includedStructureComponents;

    const latest = calculateComponentBreakup(structureComponents, nextDraft, {});
    setDraft(syncDraftTotals(nextDraft, latest));

    try {
      const savedDraft = await getEmployeeCTCDraft(employee.employeeCode, buildContext(currentRoleContext));
      if (savedDraft) {
        setCurrentCTC(null);
        applyEmployeeCTCDraftToForm(savedDraft, "saved draft");
        // Keep EmployeeMaster as the source of truth for identity, organization, PayGrade, and currency.
        // Saved drafts may contain older emails/pay grades from previous tests.
        setDraft((previous) => syncDraftTotals({
          ...previous,
          ...employeeMasterPatch,
          EmployeeCTCId: previous.EmployeeCTCId || employeeMasterPatch.EmployeeCTCId,
          RevisionNumber: previous.RevisionNumber || nextRevisionNumber,
          PreviousEmployeeCTCId: previous.PreviousEmployeeCTCId || "",
        }, calculateComponentBreakup(structureComponents, {
          ...previous,
          ...employeeMasterPatch,
        }, manualComponentOverrides)));
        showMessage(`Loaded saved draft for ${employee.employeeCode}; EmployeeMaster identity/org/pay-grade values were refreshed.`, "success");
        return;
      }

      const current = await getCurrentEmployeeCTC(employee.employeeCode, buildContext(currentRoleContext));
      if (current) {
        setCurrentCTC(current);
        applyCurrentCTCRevisionToForm(current, employee, "current approved CTC");
        setDraft((previous) => ({
          ...previous,
          ...employeeMasterPatch,
          EmployeeCTCId: previous.EmployeeCTCId || buildEmployeeCTCId(employee.employeeCode, previous.RevisionNumber || nextRevisionNumber),
        }));
      }
    } catch (error) {
      console.warn("Current CTC lookup skipped:", error);
    }
  };

  const handleManualComponentAmountChange = (componentCode, value) => {
    setManualComponentOverrides((prev) => {
      const nextOverrides = { ...prev, [componentCode]: value };
      const latest = calculateComponentBreakup(includedStructureComponents, draft, nextOverrides);
      setDraft((draftPrev) => syncDraftTotals(draftPrev, latest));
      return nextOverrides;
    });
  };

  const handleOptionalComponentToggle = (componentCode, checked) => {
    const code = normalizeText(componentCode).toUpperCase();
    setSelectedOptionalComponentCodes((prev) => {
      const next = { ...prev, [code]: checked };
      const nextIncluded = selectedStructureComponents.filter((component) => shouldIncludeComponent(component, next));
      const latest = calculateComponentBreakup(nextIncluded, draft, manualComponentOverrides);
      setDraft((draftPrev) => syncDraftTotals(draftPrev, latest));
      return next;
    });
  };

  const applySuggestedVariableReallocation = () => {
    const shortfall = toNumber(decisionPanel.shortfallAnnual, 0);
    if (!shortfall || shortfall <= 0) {
      showMessage("No target take-home shortfall is available for variable reallocation.", "error");
      return;
    }
    const flexibleVariables = calculatedBreakup.rows.filter((row) => row.applicabilityType === "Variable" && row.hrSelectable && toNumber(row.annualAmount, 0) > 0);
    if (!flexibleVariables.length) {
      showMessage("No HR-selectable variable component is available for adjustment. Mark Variable Pay as HR Selectable in CTC Component setup.", "error");
      return;
    }
    let remaining = shortfall;
    const nextOverrides = { ...manualComponentOverrides };
    flexibleVariables.forEach((row) => {
      if (remaining <= 0) return;
      const currentAmount = toNumber(nextOverrides[row.componentCode] !== undefined ? nextOverrides[row.componentCode] : row.annualAmount, 0);
      const reduction = Math.min(currentAmount, remaining);
      nextOverrides[row.componentCode] = roundMoney(currentAmount - reduction);
      remaining = roundMoney(remaining - reduction);
    });
    if (remaining > 0) {
      showMessage(`Only partial adjustment possible. Remaining shortfall is ${formatMoney(remaining, draft.SalaryCurrency)}.`, "error");
    } else {
      showMessage(`Suggested reallocation applied. Variable pay reduced by ${formatMoney(shortfall, draft.SalaryCurrency)} and balancing allowance will absorb the amount.`, "success");
    }
    setManualComponentOverrides(nextOverrides);
    const latest = calculateComponentBreakup(includedStructureComponents, draft, nextOverrides);
    setDraft((draftPrev) => syncDraftTotals(draftPrev, latest));
  };

  const recalculateDraftFromComponents = () => {
    const latest = calculateComponentBreakup(includedStructureComponents, draft, manualComponentOverrides);
    setDraft((prev) => syncDraftTotals(prev, latest));
  };

  const handleDraftDateFocus = (event) => {
    const { name } = event.target;
    if (name !== "EffectiveTo") return;
    setDraft((prev) => {
      if (!prev.EffectiveFrom || prev.EffectiveTo) return prev;
      return { ...prev, EffectiveTo: addDaysToDateInput(prev.EffectiveFrom, 365) };
    });
  };

  const handleDraftChange = (event) => {
    const { name, value, type, checked } = event.target;
    setDraft((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      if (name === "EffectiveFrom" && value && !prev.EffectiveTo) {
        next.EffectiveTo = addDaysToDateInput(value, 365);
      }
      if (["AnnualCTC", "AnnualGross", "CTCStructureId", "EffectiveFrom"].includes(name)) {
        const latest = calculateComponentBreakup(
          name === "CTCStructureId" ? (ctcMasterData.ctcComponents || []).filter((item) => item.ctcStructureId === value && shouldIncludeComponent(item, selectedOptionalComponentCodes)) : includedStructureComponents,
          next,
          manualComponentOverrides
        );
        return syncDraftTotals(next, latest);
      }
      if (name === "TargetMonthlyNetPay") {
        const calculatedAnnualCTC = backCalculateAnnualCTCForTargetNet(includedStructureComponents, next, manualComponentOverrides, value);
        if (calculatedAnnualCTC) {
          const draftWithAnnualCTC = { ...next, AnnualCTC: calculatedAnnualCTC };
          const latest = calculateComponentBreakup(includedStructureComponents, draftWithAnnualCTC, manualComponentOverrides);
          return syncDraftTotals(draftWithAnnualCTC, latest);
        }
      }
      return next;
    });

    if (name === "CTCStructureId") {
      setSelectedStructureId(value);
      setComponentForm((prev) => ({ ...prev, CTCStructureId: value }));
      setManualComponentOverrides({});
      setSelectedOptionalComponentCodes({});
    }
  };

  useEffect(() => {
    if (!draft.CTCStructureId || !includedStructureComponents.length) return;
    const latest = calculateComponentBreakup(includedStructureComponents, draft, manualComponentOverrides);
    setDraft((prev) => {
      if (prev.ComponentBreakupJson === latest.json && String(prev.MonthlyCTC || "") === String(latest.totals.MonthlyCTC || "")) return prev;
      return syncDraftTotals(prev, latest);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedStructureComponents, manualComponentOverrides]);

  const validateStructure = () => {
    const required = ["CTCStructureId", "StructureName", "CountryCode", "IndustryType", "EmploymentType", "PayFrequency", "DefaultCurrency", "StructureBasis", "EffectiveFrom"];
    const missing = required.filter((field) => !String(structureForm[field] || "").trim());
    if (missing.length) return `Missing required structure fields: ${missing.join(", ")}`;
    return "";
  };

  const validateComponent = () => {
    const required = ["CTCComponentId", "CTCStructureId", "ComponentCode", "ComponentName", "CountryCode", "ComponentCategory", "CalculationMethod", "EffectiveFrom"];
    const missing = required.filter((field) => !String(componentForm[field] || "").trim());
    if (missing.length) return `Missing required component fields: ${missing.join(", ")}`;

    const duplicateComponentCode = isDuplicateComponentCode(ctcMasterData.ctcComponents || [], componentForm.CTCStructureId, componentForm.ComponentCode);
    const editingSameComponentCode = setupEditorMode === "edit" &&
      selectedComponent?.ctcComponentId === componentForm.CTCComponentId &&
      normalizeText(selectedComponent?.ctcStructureId).toLowerCase() === normalizeText(componentForm.CTCStructureId).toLowerCase() &&
      normalizeText(selectedComponent?.componentCode).toLowerCase() === normalizeText(componentForm.ComponentCode).toLowerCase();

    if (duplicateComponentCode && !editingSameComponentCode) {
      return `ComponentCode ${componentForm.ComponentCode} already exists under ${componentForm.CTCStructureId}. One CTC Structure can have multiple components, but each ComponentCode must be unique under the same structure.`;
    }

    return "";
  };

  const currentPayScaleValidation = useMemo(() => {
    if (!draft?.PayGrade) return null;
    return validateEmployeeCTCAgainstPayScale(draft, payScaleBands);
  }, [draft, payScaleBands]);

  const validateDraft = (draftValue = draft) => {
    const required = ["EmployeeCTCId", "EmployeeCode", "CountryCode", "LegalEntityCode", "CTCStructureId", "PayFrequency", "SalaryCurrency", "EffectiveFrom", "ComponentBreakupJson"];
    const missing = required.filter((field) => !String(draftValue[field] || "").trim());
    if (missing.length) return `Missing required CTC draft fields: ${missing.join(", ")}`;
    try {
      JSON.parse(draftValue.ComponentBreakupJson || "[]");
    } catch {
      return "ComponentBreakupJson must be valid JSON.";
    }
    const payScaleValidation = validateEmployeeCTCAgainstPayScale(draftValue, payScaleBands);
    if (!payScaleValidation.valid) return payScaleValidation.message;
    return "";
  };

  const handleSaveStructure = async () => {
    if (!canEditCTCSetup || setupEditorMode === "view") {
      showMessage("You have view-only access for CTC Structure setup.", "error");
      return;
    }
    const validation = validateStructure();
    if (validation) {
      showMessage(validation, "error");
      return;
    }
    try {
      setSaving(true);
      const result = await saveCTCStructure(structureForm, {
        changedBy: currentUserEmail,
        changeReason: "CTC structure created from Compensation setup",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC structure saved successfully.");
      const refreshed = await refreshCTCMasterData(orgFilter);
      const selectedEntity = hrMasterData.legalEntities.find((item) => item.value === orgFilter.LegalEntityCode);
      setStructureForm(createStructureFormForFilter(orgFilter, refreshed.ctcStructures || [], selectedEntity));
      setSelectedStructureId(structureForm.CTCStructureId);
      setSelectedComponentId("");
      setComponentForm(createComponentFormForFilter(orgFilter, structureForm.CTCStructureId, refreshed.ctcComponents || []));
      setDraft((prev) => ({ ...createDraftForFilter(orgFilter, selectedEntity), CTCStructureId: structureForm.CTCStructureId }));
      closeSetupEditor();
    } catch (error) {
      console.error("Save CTC structure error:", error);
      showMessage(error.message || "Failed to save CTC structure.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveComponent = async () => {
    if (!canEditCTCSetup || setupEditorMode === "view") {
      showMessage("You have view-only access for CTC Component setup.", "error");
      return;
    }
    const validation = validateComponent();
    if (validation) {
      showMessage(validation, "error");
      return;
    }
    try {
      setSaving(true);
      const result = await saveCTCComponent(componentForm, {
        changedBy: currentUserEmail,
        changeReason: "CTC component created from Compensation setup",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC component saved successfully.");
      const refreshed = await refreshCTCMasterData(orgFilter);
      setSelectedStructureId(componentForm.CTCStructureId);
      setSelectedComponentId("");
      setComponentForm(createComponentFormForFilter(orgFilter, componentForm.CTCStructureId, refreshed.ctcComponents || []));
      closeSetupEditor();
    } catch (error) {
      console.error("Save CTC component error:", error);
      showMessage(error.message || "Failed to save CTC component.", "error");
    } finally {
      setSaving(false);
    }
  };

  const loadEmployeeCTC = async (code = employeeCode) => {
    if (!String(code || "").trim()) {
      showMessage("Employee Code is required to load CTC.", "error");
      return;
    }
    try {
      setLoading(true);
      showMessage("");
      const [savedDraft, current, history] = await Promise.all([
        getEmployeeCTCDraft(code, buildContext(currentRoleContext)),
        getCurrentEmployeeCTC(code, buildContext(currentRoleContext)),
        getEmployeeCTCHistory(code, buildContext(currentRoleContext)),
      ]);
      setCurrentCTC(current);
      setHistoryRows(history);
      if (savedDraft) {
        applyEmployeeCTCDraftToForm(savedDraft, "saved draft");
      } else if (current) {
        const selectedEmployee = normalizedEmployees.find((item) => item.employeeCode === code) || null;
        applyCurrentCTCRevisionToForm(current, selectedEmployee, "current approved CTC");
      } else {
        setDraft((prev) => ({ ...prev, EmployeeCode: code }));
      }
    } catch (error) {
      console.error("Load employee CTC error:", error);
      showMessage(error.message || "Failed to load employee CTC.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    const validation = validateDraft();
    if (validation) {
      showMessage(validation, "error");
      return;
    }
    try {
      setSaving(true);
      showMessage("");
      const latestBreakup = calculateComponentBreakup(includedStructureComponents, draft, manualComponentOverrides);
      const ensuredEmployeeCTCId = draft.EmployeeCTCId || buildEmployeeCTCId(draft.EmployeeCode, draft.RevisionNumber);
      const draftWithId = ensureEffectiveToFromEffectiveFrom({ ...draft, EmployeeCTCId: ensuredEmployeeCTCId });
      const payload = cleanEmployeeCTCPayload({ ...syncDraftTotals(draftWithId, latestBreakup), ComponentBreakupJson: safeJsonString(latestBreakup.json), SourceModule: "CTCConsole" });
      const result = await saveEmployeeCTCDraft(payload, {
        changedBy: currentUserEmail,
        changeReason: draft.RevisionReason || "CTC draft saved from frontend",
        context: buildContext(currentRoleContext),
      });
      setDraft({ ...payload, EmployeeCTCId: result?.employeeCTCId || result?.EmployeeCTCId || ensuredEmployeeCTCId });
      showMessage(result?.message || "Employee CTC draft saved successfully.");
    } catch (error) {
      console.error("Save CTC draft error:", error);
      showMessage(error.message || "Failed to save CTC draft.", "error");
    } finally {
      setSaving(false);
    }
  };

  const refreshPendingApprovals = async () => {
    const rows = await getPendingCTCApprovals({}, buildContext(currentRoleContext));
    setPendingApprovals(rows);
    return rows;
  };

  const handleSubmit = async () => {
    let id = draft.EmployeeCTCId || currentCTC?.employeeCTCId || selectedPendingId;
    try {
      setSaving(true);
      const latestBreakup = calculateComponentBreakup(includedStructureComponents, draft, manualComponentOverrides);
      const draftForValidation = ensureEffectiveToFromEffectiveFrom({ ...draft, EmployeeCTCId: id || draft.EmployeeCTCId || buildEmployeeCTCId(draft.EmployeeCode, draft.RevisionNumber) });
      const validation = validateDraft(draftForValidation);
      if (validation) {
        showMessage(validation, "error");
        return;
      }
      let submitPayload = cleanEmployeeCTCPayload({ ...syncDraftTotals(draftForValidation, latestBreakup), ComponentBreakupJson: safeJsonString(latestBreakup.json), SourceModule: "CTCConsole" });
      if (!id) {
        id = submitPayload.EmployeeCTCId;
        const saveResult = await saveEmployeeCTCDraft(submitPayload, {
          changedBy: currentUserEmail,
          changeReason: draft.RevisionReason || "CTC draft saved before submit from frontend",
          context: buildContext(currentRoleContext),
        });
        id = saveResult?.employeeCTCId || saveResult?.EmployeeCTCId || id;
        submitPayload = { ...submitPayload, EmployeeCTCId: id };
        setDraft(submitPayload);
      }
      submitPayload = cleanEmployeeCTCPayload(ensureEffectiveToFromEffectiveFrom({ ...submitPayload, EmployeeCTCId: id }));
      const result = await submitEmployeeCTC(id, submitPayload, {
        changedBy: currentUserEmail,
        changeReason: "Submitted from CTC Console",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC submitted successfully.");
      await refreshPendingApprovals();
      if (draft.EmployeeCode || employeeCode) await loadEmployeeCTC(draft.EmployeeCode || employeeCode);
    } catch (error) {
      console.error("Submit CTC error:", error);
      showMessage(error.message || "Failed to submit CTC.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!canApproveCTC) {
      showMessage("You have view-only access for CTC approvals.", "error");
      return;
    }
    const id = selectedPendingId || selectedPending?.employeeCTCId;
    if (!id) {
      showMessage("Select a pending CTC approval row first.", "error");
      return;
    }
    try {
      setSaving(true);
      const result = await approveEmployeeCTC(id, { EmployeeCTCId: id, ApprovalRemarks: approvalRemarks, ApproverRemarks: approvalRemarks, Remarks: approvalRemarks || selectedPending?.remarks || "" }, {
        changedBy: currentUserEmail,
        changeReason: approvalRemarks || "Approved from CTC Console",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC approved successfully.");
      setSelectedPendingId("");
      setApprovalRemarks("");
      await refreshPendingApprovals();
      if (selectedPending?.employeeCode) await loadEmployeeCTC(selectedPending.employeeCode);
    } catch (error) {
      console.error("Approve CTC error:", error);
      showMessage(error.message || "Failed to approve CTC.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!canApproveCTC) {
      showMessage("You have view-only access for CTC approvals.", "error");
      return;
    }
    const id = selectedPendingId || selectedPending?.employeeCTCId;
    if (!id) {
      showMessage("Select a pending CTC approval row first.", "error");
      return;
    }
    if (!rejectionReason.trim()) {
      showMessage("Rejection reason is required.", "error");
      return;
    }
    try {
      setSaving(true);
      const result = await rejectEmployeeCTC(id, rejectionReason, { EmployeeCTCId: id, RejectionReason: rejectionReason, ApprovalRemarks: approvalRemarks, ApproverRemarks: approvalRemarks, Remarks: approvalRemarks || rejectionReason }, {
        changedBy: currentUserEmail,
        changeReason: approvalRemarks ? `${rejectionReason} | Approver remarks: ${approvalRemarks}` : rejectionReason,
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC rejected successfully.");
      setSelectedPendingId("");
      setRejectionReason("");
      setApprovalRemarks("");
      await refreshPendingApprovals();
      if (selectedPending?.employeeCode) await loadEmployeeCTC(selectedPending.employeeCode);
    } catch (error) {
      console.error("Reject CTC error:", error);
      showMessage(error.message || "Failed to reject CTC.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSendBackForCorrection = async () => {
    if (!canApproveCTC) {
      showMessage("You have view-only access for CTC approvals.", "error");
      return;
    }
    const id = selectedPendingId || selectedPending?.employeeCTCId;
    const correctionReason = rejectionReason.trim() || approvalRemarks.trim();
    if (!id) {
      showMessage("Select a pending CTC approval row first.", "error");
      return;
    }
    if (!correctionReason) {
      showMessage("Correction reason is required for Send Back for Correction.", "error");
      return;
    }
    try {
      setSaving(true);
      const result = await sendBackEmployeeCTC(id, correctionReason, {
        EmployeeCTCId: id,
        CorrectionReason: correctionReason,
        ApprovalRemarks: approvalRemarks,
        ApproverRemarks: approvalRemarks,
        Remarks: approvalRemarks || correctionReason,
      }, {
        changedBy: currentUserEmail,
        changeReason: approvalRemarks ? `${correctionReason} | Approver remarks: ${approvalRemarks}` : correctionReason,
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC sent back for correction successfully.");
      setSelectedPendingId("");
      setRejectionReason("");
      setApprovalRemarks("");
      await refreshPendingApprovals();
      if (selectedPending?.employeeCode) await loadEmployeeCTC(selectedPending.employeeCode);
    } catch (error) {
      console.error("Send back CTC error:", error);
      showMessage(error.message || "Failed to send CTC back for correction.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkRevisionDue = async () => {
    const code = employeeCode || currentCTC?.employeeCode || draft.EmployeeCode;
    if (!code) {
      showMessage("Employee Code is required to mark revision due.", "error");
      return;
    }
    try {
      setSaving(true);
      const result = await markCTCRevisionDue(code, { EmployeeCode: code }, {
        changedBy: currentUserEmail,
        changeReason: "Revision due marked from CTC Console",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC marked revision due successfully.");
      await loadEmployeeCTC(code);
    } catch (error) {
      console.error("Mark revision due error:", error);
      showMessage(error.message || "Failed to mark revision due.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelDraft = async () => {
    const id = draft.EmployeeCTCId || selectedPendingId;
    if (!id) {
      showMessage("EmployeeCTCId is required to cancel draft.", "error");
      return;
    }
    try {
      setSaving(true);
      const result = await cancelCTCDraft(id, { EmployeeCTCId: id }, {
        changedBy: currentUserEmail,
        changeReason: "Draft cancelled from CTC Console",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "CTC draft cancelled successfully.");
      await refreshPendingApprovals();
      if (draft.EmployeeCode || employeeCode) await loadEmployeeCTC(draft.EmployeeCode || employeeCode);
    } catch (error) {
      console.error("Cancel CTC draft error:", error);
      showMessage(error.message || "Failed to cancel CTC draft.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDataQuality = async () => {
    try {
      setLoading(true);
      const result = await runCTCDataQualityCheck({}, buildContext(currentRoleContext));
      setQualityData(result);
      showMessage("CTC data quality source rows loaded.");
    } catch (error) {
      console.error("CTC data quality error:", error);
      showMessage(error.message || "Failed to run CTC data quality check.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStructureSelectForComponent = (event) => {
    const structureId = event.target.value;
    const selected = availableStructures.find((item) => item.ctcStructureId === structureId);

    setComponentForm((prev) => ({
      ...createComponentFormForFilter(orgFilter, structureId, ctcMasterData.ctcComponents || []),
      ComponentCode: prev.ComponentCode,
      ComponentName: prev.ComponentName,
      ComponentCategory: prev.ComponentCategory,
      ComponentSubCategory: prev.ComponentSubCategory,
      CalculationMethod: prev.CalculationMethod,
      CountryCode: selected?.countryCode || orgFilter.CountryCode,
      CTCComponentId: prev.ComponentCode ? buildSuggestedComponentId(structureId, prev.ComponentCode) : "",
    }));

    setDraft((prev) => ({
      ...prev,
      CTCStructureId: structureId,
      PayFrequency: selected?.payFrequency || prev.PayFrequency,
      SalaryCurrency: selected?.defaultCurrency || prev.SalaryCurrency,
    }));
  };

  const hydrateStructureForm = (row = null) => {
    const raw = row?.raw || row || {};
    return {
      ...EMPTY_STRUCTURE_FORM,
      CTCStructureId: row?.ctcStructureId || getField(raw, "CTCStructureId", "field_1") || "",
      StructureName: row?.structureName || getField(raw, "StructureName", "field_2") || "",
      CountryCode: row?.countryCode || getField(raw, "CountryCode", "field_3") || "IN",
      RegionGroup: getField(raw, "RegionGroup", "field_4") || (row?.countryCode === "DE" ? "EMEA" : "APAC"),
      LegalEntityCode: getField(raw, "LegalEntityCode", "field_5") || "",
      LocationCode: getField(raw, "LocationCode", "field_6") || "",
      DepartmentCode: row?.departmentCode || getField(raw, "DepartmentCode", "field_27") || "",
      WorkUnitCode: row?.workUnitCode || getField(raw, "WorkUnitCode", "field_28") || "",
      IndustryType: row?.industryType || getField(raw, "IndustryType", "field_7") || EMPTY_STRUCTURE_FORM.IndustryType,
      EmploymentType: row?.employmentType || getField(raw, "EmploymentType", "field_8") || EMPTY_STRUCTURE_FORM.EmploymentType,
      WorkArrangement: getField(raw, "WorkArrangement", "field_9") || EMPTY_STRUCTURE_FORM.WorkArrangement,
      PayFrequency: row?.payFrequency || getField(raw, "PayFrequency", "field_10") || EMPTY_STRUCTURE_FORM.PayFrequency,
      DefaultCurrency: row?.defaultCurrency || getField(raw, "DefaultCurrency", "field_11") || EMPTY_STRUCTURE_FORM.DefaultCurrency,
      StructureBasis: row?.structureBasis || getField(raw, "StructureBasis", "field_12") || EMPTY_STRUCTURE_FORM.StructureBasis,
      IsStatutoryStructure: isTruthy(getField(raw, "IsStatutoryStructure", "field_13")),
      SupportsVariablePay: isTruthy(getField(raw, "SupportsVariablePay", "field_14")),
      SupportsHourlyRates: isTruthy(getField(raw, "SupportsHourlyRates", "field_15")),
      SupportsShiftAllowance: isTruthy(getField(raw, "SupportsShiftAllowance", "field_16")),
      SupportsShiftHours: isTruthy(getField(raw, "SupportsShiftHours", "SupportShiftHours")) || isTruthy(getField(raw, "SupportsShiftAllowance", "field_16")),
      RequiresApproval: isTruthy(getField(raw, "RequiresApproval", "field_17")),
      EffectiveFrom: getField(raw, "EffectiveFrom", "field_18") || "",
      EffectiveTo: getField(raw, "EffectiveTo", "field_19") || "",
      Status: row?.status || getField(raw, "Status", "field_20") || "Active",
      IsDefault: row?.isDefault || isTruthy(getField(raw, "IsDefault", "field_21")),
      IsActive: row?.isActive !== undefined ? row.isActive : isTruthy(getField(raw, "IsActive", "field_22")),
      DisplayOrder: row?.displayOrder || getField(raw, "DisplayOrder", "field_23") || 10,
      Notes: getField(raw, "Notes", "field_24") || "",
    };
  };

  const hydrateComponentForm = (row = null) => {
    const raw = row?.raw || row || {};
    return {
      ...EMPTY_COMPONENT_FORM,
      CTCComponentId: row?.ctcComponentId || getField(raw, "CTCComponentId", "field_1") || "",
      CTCStructureId: row?.ctcStructureId || getField(raw, "CTCStructureId", "field_2") || "",
      ComponentCode: row?.componentCode || getField(raw, "ComponentCode", "field_3") || "",
      ComponentName: row?.componentName || getField(raw, "ComponentName", "field_4") || "",
      CountryCode: row?.countryCode || getField(raw, "CountryCode", "field_5") || orgFilter.CountryCode || "IN",
      ComponentCategory: row?.componentCategory || getField(raw, "ComponentCategory", "field_6") || "Earning",
      ComponentSubCategory: row?.componentSubCategory || getField(raw, "ComponentSubCategory", "field_7") || "Basic",
      CalculationMethod: row?.calculationMethod || getField(raw, "CalculationMethod", "field_8") || "FixedAmount",
      CalculationBaseComponentCode: getField(raw, "CalculationBaseComponentCode", "field_9") || "",
      DefaultPercentage: row?.defaultPercentage || getField(raw, "DefaultPercentage", "field_10") || "",
      DefaultAmount: row?.defaultAmount || getField(raw, "DefaultAmount", "field_11") || "",
      MinimumAmount: getField(raw, "MinimumAmount", "field_12") || "",
      MaximumAmount: getField(raw, "MaximumAmount", "field_13") || "",
      IsTaxable: getField(raw, "IsTaxable", "field_14") || "Yes",
      IsStatutory: isTruthy(getField(raw, "IsStatutory", "field_15")),
      IsEmployerCost: isTruthy(getField(raw, "IsEmployerCost", "field_16")),
      IsEmployeeDeduction: isTruthy(getField(raw, "IsEmployeeDeduction", "field_17")),
      IsCashComponent: getField(raw, "IsCashComponent", "field_18") === "" ? true : isTruthy(getField(raw, "IsCashComponent", "field_18")),
      IsVariable: isTruthy(getField(raw, "IsVariable", "field_19")),
      IsProrated: getField(raw, "IsProrated", "field_20") === "" ? true : isTruthy(getField(raw, "IsProrated", "field_20")),
      ProrationMethod: getField(raw, "ProrationMethod", "field_21") || "CalendarDays",
      IncludeInAnnualCTC: getField(raw, "IncludeInAnnualCTC", "field_22") === "" ? true : isTruthy(getField(raw, "IncludeInAnnualCTC", "field_22")),
      IncludeInMonthlyGross: getField(raw, "IncludeInMonthlyGross", "field_23") === "" ? true : isTruthy(getField(raw, "IncludeInMonthlyGross", "field_23")),
      IncludeInNetPayEstimate: getField(raw, "IncludeInNetPayEstimate", "field_24") === "" ? true : isTruthy(getField(raw, "IncludeInNetPayEstimate", "field_24")),
      DisplayGroup: getField(raw, "DisplayGroup", "field_25") || "Earnings",
      DisplayOrder: row?.displayOrder || getField(raw, "DisplayOrder", "field_26") || 10,
      EffectiveFrom: getField(raw, "EffectiveFrom", "field_27") || "",
      EffectiveTo: getField(raw, "EffectiveTo", "field_28") || "",
      Status: row?.status || getField(raw, "Status", "field_29") || "Active",
      IsActive: row?.isActive !== undefined ? row.isActive : isTruthy(getField(raw, "IsActive", "field_30")),
      ApplicabilityType: row?.applicabilityType || getField(raw, "ApplicabilityType", "field_32") || getApplicabilityType(row || raw),
      HRSelectable: row?.hrSelectable !== undefined ? row.hrSelectable : isTruthy(getField(raw, "HRSelectable", "field_33")),
      Notes: getField(raw, "Notes", "field_31") || "",
    };
  };

  const handlePayScaleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPayScaleForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const openAddPayScaleBand = () => {
    if (!canEditCTCSetup) {
      showMessage("You have view-only access for PayBand setup.", "error");
      return;
    }
    setPayScaleForm(buildPayScaleFormForFilter(orgFilter, payScaleBands));
    setSelectedPayScaleBandId("");
    setPayScaleEditorMode("add");
    setPayScaleEditorOpen(true);
  };

  const openEditPayScaleBand = (row = selectedPayScaleBand, mode = "edit") => {
    if (!row) {
      showMessage("Select a PayBand / PayGrade row first.", "error");
      return;
    }
    setSelectedPayScaleBandId(String(row.id || row.ID || row.payGradeCode));
    setPayScaleForm(hydratePayScaleForm(row, orgFilter));
    setPayScaleEditorMode(mode === "view" ? "view" : "edit");
    setPayScaleEditorOpen(true);
  };

  const closePayScaleEditor = () => {
    setPayScaleEditorOpen(false);
    setPayScaleEditorMode("add");
  };

  const validatePayScaleForm = () => {
    const required = ["PayGradeCode", "GradeName", "CountryCode", "Currency", "MinAnnualCTC", "MidAnnualCTC", "MaxAnnualCTC", "EffectiveFrom"];
    const missing = required.filter((field) => !String(payScaleForm[field] || "").trim());
    if (missing.length) return `Missing required PayBand fields: ${missing.join(", ")}`;
    if (toNumber(payScaleForm.MinAnnualCTC, 0) > toNumber(payScaleForm.MidAnnualCTC, 0)) return "MinAnnualCTC cannot be greater than MidAnnualCTC.";
    if (toNumber(payScaleForm.MidAnnualCTC, 0) > toNumber(payScaleForm.MaxAnnualCTC, 0)) return "MidAnnualCTC cannot be greater than MaxAnnualCTC.";
    return "";
  };

  const handleSavePayScaleBand = async () => {
    if (!canEditCTCSetup || payScaleEditorMode === "view") {
      showMessage("You have view-only access for PayBand setup.", "error");
      return;
    }
    const validation = validatePayScaleForm();
    if (validation) {
      showMessage(validation, "error");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...payScaleForm,
        Title: payScaleForm.PayGradeCode,
        MinAnnualCTC: toNumber(payScaleForm.MinAnnualCTC, 0),
        MidAnnualCTC: toNumber(payScaleForm.MidAnnualCTC, 0),
        MaxAnnualCTC: toNumber(payScaleForm.MaxAnnualCTC, 0),
        MinMonthlyGross: toNumber(payScaleForm.MinMonthlyGross, 0),
        MaxMonthlyGross: toNumber(payScaleForm.MaxMonthlyGross, 0),
        DisplayOrder: toNumber(payScaleForm.DisplayOrder, 999),
      };
      const result = await savePayScaleBand(payload, {
        changedBy: currentUserEmail,
        changeReason: "PayBand saved from Compensation setup",
        context: buildContext(currentRoleContext),
      });
      showMessage(result?.message || "PayBand / PayGrade saved successfully.");
      await refreshPayScaleBands(orgFilter);
      setSelectedPayScaleBandId(String(result?.id || result?.ID || payScaleForm.ID || payScaleForm.PayGradeCode));
      closePayScaleEditor();
    } catch (error) {
      console.error("Save PayBand error:", error);
      showMessage(error.message || "Failed to save PayBand / PayGrade.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddStructure = () => {
    if (!canEditCTCSetup) {
      showMessage("You have view-only access for CTC Structure setup.", "error");
      return;
    }
    const selectedEntity = hrMasterData.legalEntities.find((item) => item.value === orgFilter.LegalEntityCode);
    setStructureForm(createStructureFormForFilter(orgFilter, ctcMasterData.ctcStructures || [], selectedEntity));
    setSetupEditorType("structure");
    setSetupEditorMode("add");
    setSetupEditorOpen(true);
  };

  const openEditStructure = (row = selectedStructure, mode = "edit") => {
    if (!row) {
      showMessage("Select a CTC Structure first.", "error");
      return;
    }
    setSelectedStructureId(row.ctcStructureId);
    setStructureForm(hydrateStructureForm(row));
    setSetupEditorType("structure");
    setSetupEditorMode(mode === "view" ? "view" : "edit");
    setSetupEditorOpen(true);
  };

  const openAddComponent = () => {
    if (!canEditCTCSetup) {
      showMessage("You have view-only access for CTC Component setup.", "error");
      return;
    }
    const targetStructureId = selectedStructureId || componentForm.CTCStructureId || availableStructures[0]?.ctcStructureId || "";
    setComponentForm(createComponentFormForFilter(orgFilter, targetStructureId, ctcMasterData.ctcComponents || []));
    setSetupEditorType("component");
    setSetupEditorMode("add");
    setSetupEditorOpen(true);
  };

  const openEditComponent = (row = selectedComponent, mode = "edit") => {
    if (!row) {
      showMessage("Select a CTC Component first.", "error");
      return;
    }
    setSelectedComponentId(row.ctcComponentId);
    setComponentForm(hydrateComponentForm(row));
    setSetupEditorType("component");
    setSetupEditorMode(mode === "view" ? "view" : "edit");
    setSetupEditorOpen(true);
  };

  const closeSetupEditor = () => {
    setSetupEditorOpen(false);
    setSetupEditorType("");
    setSetupEditorMode("add");
  };

  const cancelSetupEditor = () => {
    closeSetupEditor();
    showMessage("");
  };

  const handleSelectCatalogueComponent = (catalogueRow = {}) => {
    if (!canEditCTCSetup) {
      showMessage("You have view-only access for CTC Component setup.", "error");
      return;
    }
    const targetStructureId = selectedStructureId || componentForm.CTCStructureId || availableStructures[0]?.ctcStructureId || "";
    if (!targetStructureId) {
      showMessage("Select or create a CTC Structure before adding catalogue components.", "error");
      return;
    }
    const next = {
      ...createComponentFormForFilter(orgFilter, targetStructureId, ctcMasterData.ctcComponents || []),
      CTCStructureId: targetStructureId,
      ComponentCode: catalogueRow.componentCode || "",
      ComponentName: catalogueRow.componentName || "",
      CountryCode: catalogueRow.countryCode || orgFilter.CountryCode || "IN",
      ComponentCategory: catalogueRow.componentCategory || "Earning",
      ComponentSubCategory: catalogueRow.componentSubCategory || "Other",
      CalculationMethod: catalogueRow.calculationMethod || "Manual",
      CalculationBaseComponentCode: catalogueRow.calculationBaseComponentCode || "",
      ProrationMethod: catalogueRow.prorationMethod || "None",
      DefaultPercentage: catalogueRow.defaultPercentage || "",
      DefaultAmount: catalogueRow.defaultAmount || "",
      MinimumAmount: catalogueRow.minimumAmount || "",
      MaximumAmount: catalogueRow.maximumAmount || "",
      IsTaxable: catalogueRow.taxabilityRule || catalogueRow.isTaxable || "CountryRule",
      DisplayGroup: catalogueRow.displayGroup || "Earnings",
      DisplayOrder: getNextComponentDisplayOrder(ctcMasterData.ctcComponents || [], targetStructureId),
      ApplicabilityType: catalogueRow.applicabilityType || "Mandatory",
      HRSelectable: Boolean(catalogueRow.hrSelectable),
      IsStatutory: catalogueRow.applicabilityType === "Statutory",
      IsEmployerCost: Boolean(catalogueRow.isEmployerCost),
      IsEmployeeDeduction: Boolean(catalogueRow.isEmployeeDeduction),
      IsCashComponent: Boolean(catalogueRow.isCashComponent),
      IsVariable: Boolean(catalogueRow.isVariable) || catalogueRow.applicabilityType === "Variable",
      IsProrated: Boolean(catalogueRow.isProrated),
      IncludeInAnnualCTC: Boolean(catalogueRow.includeInAnnualCTC),
      IncludeInMonthlyGross: Boolean(catalogueRow.includeInMonthlyGross),
      IncludeInNetPayEstimate: Boolean(catalogueRow.includeInNetPayEstimate),
      EffectiveFrom: catalogueRow.effectiveFrom || structureForm.EffectiveFrom || "",
      EffectiveTo: catalogueRow.effectiveTo || "",
      Status: "Active",
      IsActive: true,
      Notes: `${catalogueRow.notes || ""}${catalogueRow.requiresPayrollValidation ? "\nPayroll/legal validation required before production use." : ""}`.trim(),
    };
    next.CTCComponentId = buildSuggestedComponentId(targetStructureId, next.ComponentCode);
    setComponentForm(next);
    setSelectedStructureId(targetStructureId);
    setSetupEditorType("component");
    setSetupEditorMode("add");
    setSetupEditorOpen(true);
    showMessage(`Catalogue component ${next.ComponentCode} loaded into Add Component editor. Review and save.`, "success");
  };
  const renderCatalogueRows = (title, rows) => (
    <div style={styles.catalogueGroupBox}>
      <div style={styles.groupHeader}><h3 style={styles.groupTitle}>{title}</h3><span style={styles.pill}>{rows.length}</span></div>
      <div style={styles.catalogueTableWrapper}>
        <table style={styles.compactTable}>
          <thead><tr><th style={styles.th}>Code</th><th style={styles.th}>Name</th><th style={styles.th}>Category</th><th style={styles.th}>Method</th><th style={styles.th}>HR Selectable</th><th style={styles.th}>Action</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="6">No catalogue components in this group for current filters.</td></tr>}
            {rows.slice(0, 40).map((row) => (
              <tr key={row.catalogueComponentId || `${title}-${row.componentCode}`}>
                <td style={styles.td}>{displayText(row.componentCode)}</td>
                <td style={styles.td}>{displayText(row.componentName)}</td>
                <td style={styles.td}>{displayText(row.componentCategory)}</td>
                <td style={styles.td}>{displayText(row.calculationMethod)}</td>
                <td style={styles.td}>{row.hrSelectable ? "Yes" : "No"}</td>
                <td style={styles.td}><button type="button" style={styles.rowButton} onClick={() => handleSelectCatalogueComponent(row)}>Select</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  const renderPayScaleBandEditor = () => (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>{payScaleEditorMode === "add" ? "Add PayBand / PayGrade" : payScaleEditorMode === "view" ? "View PayBand / PayGrade" : "Edit PayBand / PayGrade"}</h2>
          <p style={styles.panelSubtitle}>List: HR_PayScaleBands · Controlled by country, legal entity, industry and employment type.</p>
        </div>
        <div style={styles.inlineActions}>
          <button type="button" style={styles.secondaryButton} onClick={closePayScaleEditor}>Close</button>
          {payScaleEditorMode !== "view" && <button type="button" style={styles.primaryButton} onClick={handleSavePayScaleBand} disabled={saving}>Save PayBand</button>}
        </div>
      </div>
      <div style={styles.editorGrid}>
        {renderInput("PayGrade Code", "PayGradeCode", payScaleForm.PayGradeCode, handlePayScaleFormChange, true)}
        {renderInput("Grade Name", "GradeName", payScaleForm.GradeName, handlePayScaleFormChange, true)}
        {renderSelect("Country", "CountryCode", payScaleForm.CountryCode, handlePayScaleFormChange, countryOptions, true)}
        {renderInput("Legal Entity Code", "LegalEntityCode", payScaleForm.LegalEntityCode, handlePayScaleFormChange)}
        {renderSelect("Industry Type", "IndustryType", payScaleForm.IndustryType, handlePayScaleFormChange, ["All", ...INDUSTRY_OPTIONS], true)}
        {renderSelect("Employment Type", "EmploymentType", payScaleForm.EmploymentType, handlePayScaleFormChange, ["All", ...EMPLOYMENT_TYPE_OPTIONS], true)}
        {renderSelect("Currency", "Currency", payScaleForm.Currency, handlePayScaleFormChange, CURRENCY_OPTIONS, true)}
        {renderInput("Min Annual CTC", "MinAnnualCTC", payScaleForm.MinAnnualCTC, handlePayScaleFormChange, true, "number")}
        {renderInput("Mid Annual CTC", "MidAnnualCTC", payScaleForm.MidAnnualCTC, handlePayScaleFormChange, true, "number")}
        {renderInput("Max Annual CTC", "MaxAnnualCTC", payScaleForm.MaxAnnualCTC, handlePayScaleFormChange, true, "number")}
        {renderInput("Min Monthly Gross", "MinMonthlyGross", payScaleForm.MinMonthlyGross, handlePayScaleFormChange, false, "number")}
        {renderInput("Max Monthly Gross", "MaxMonthlyGross", payScaleForm.MaxMonthlyGross, handlePayScaleFormChange, false, "number")}
        {renderInput("Effective From", "EffectiveFrom", payScaleForm.EffectiveFrom, handlePayScaleFormChange, true, "date")}
        {renderInput("Effective To", "EffectiveTo", payScaleForm.EffectiveTo, handlePayScaleFormChange, false, "date")}
        {renderInput("Display Order", "DisplayOrder", payScaleForm.DisplayOrder, handlePayScaleFormChange, false, "number")}
        {renderCheckbox("Is Active", "IsActive", payScaleForm.IsActive, handlePayScaleFormChange)}
        {renderCheckbox("Approval Required For Out-of-Band", "ApprovalRequiredForOutOfBand", payScaleForm.ApprovalRequiredForOutOfBand, handlePayScaleFormChange)}
      </div>
    </section>
  );

  const renderPayScaleBandPanel = () => (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>PayBand / PayGrade Setup</h2>
          <p style={styles.panelSubtitle}>List: HR_PayScaleBands · Define min/mid/max bands before fixing Employee CTC. EmployeeMaster.payGrade must match PayGradeCode.</p>
        </div>
        <div style={styles.inlineActions}>
          <button type="button" style={styles.secondaryButton} onClick={() => refreshPayScaleBands(orgFilter)} disabled={loading || saving}>Refresh PayBands</button>
          <button type="button" style={styles.primaryButton} onClick={openAddPayScaleBand} disabled={!canEditCTCSetup}>Add</button>
          <button type="button" style={styles.secondaryButton} onClick={() => openEditPayScaleBand(selectedPayScaleBand, "edit")} disabled={!selectedPayScaleBandId || !canEditCTCSetup}>Edit Selected</button>
        </div>
      </div>
      <div style={styles.helperBox}>Showing {filteredPayScaleBands.length} active PayBands for current controlled selection chain. Use Country/Legal Entity/Industry/Employment filters above to narrow the setup.</div>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Select</th>
              <th style={styles.th}>PayGrade</th>
              <th style={styles.th}>Grade Name</th>
              <th style={styles.th}>Country</th>
              <th style={styles.th}>Legal Entity</th>
              <th style={styles.th}>Industry</th>
              <th style={styles.th}>Employment</th>
              <th style={styles.th}>Currency</th>
              <th style={styles.th}>Min</th>
              <th style={styles.th}>Mid</th>
              <th style={styles.th}>Max</th>
              <th style={styles.th}>Approval</th>
              <th style={styles.th}>Active</th>
              <th style={styles.th}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayScaleBands.length === 0 && <tr><td style={styles.emptyCell} colSpan="14">No PayBands found for current filters. Click Add to create one.</td></tr>}
            {filteredPayScaleBands.map((row) => {
              const rowKey = String(row.id || row.ID || row.payGradeCode);
              return (
                <tr key={rowKey}>
                  <td style={styles.td}><input type="radio" name="selectedPayScaleBand" checked={selectedPayScaleBandId === rowKey} onChange={() => setSelectedPayScaleBandId(rowKey)} /></td>
                  <td style={styles.td}>{displayText(row.payGradeCode)}</td>
                  <td style={styles.td}>{displayText(row.gradeName)}</td>
                  <td style={styles.td}>{displayText(row.countryCode, "ALL")}</td>
                  <td style={styles.td}>{displayText(row.legalEntityCode, "ALL")}</td>
                  <td style={styles.td}>{displayText(row.industryType, "All")}</td>
                  <td style={styles.td}>{displayText(row.employmentType, "All")}</td>
                  <td style={styles.td}>{displayText(row.currency)}</td>
                  <td style={styles.td}>{formatMoney(row.minAnnualCTC, row.currency)}</td>
                  <td style={styles.td}>{formatMoney(row.midAnnualCTC, row.currency)}</td>
                  <td style={styles.td}>{formatMoney(row.maxAnnualCTC, row.currency)}</td>
                  <td style={styles.td}>{row.approvalRequiredForOutOfBand ? "Required" : "Warning"}</td>
                  <td style={styles.td}>{row.isActive ? "Yes" : "No"}</td>
                  <td style={styles.td}><button type="button" style={styles.rowButton} onClick={() => openEditPayScaleBand(row, "view")}>View</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderComponentCataloguePanel = () => (
    <section style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>CTC Component Catalogue</h2>
          <p style={styles.panelSubtitle}>Reusable country-wise CTC components. Select a row to prefill the existing Add Component editor; existing save design remains unchanged.</p>
        </div>
        <div style={styles.inlineActions}><button type="button" style={styles.secondaryButton} onClick={() => refreshComponentCatalogue(orgFilter)} disabled={loading || saving}>Refresh Catalogue</button></div>
      </div>
      <div style={styles.helperBox}>
        Showing {filteredComponentCatalogue.length} catalogue components for country {orgFilter.CountryCode || "ALL"}.
        Optional narrowing: {catalogueFilterByIndustry ? `Industry = ${orgFilter.IndustryType || "ALL"}` : "Industry filter OFF"} · {catalogueFilterByEmploymentType ? `Employment Type = ${orgFilter.EmploymentType || "ALL"}` : "Employment Type filter OFF"}.
      </div>
      <div style={styles.catalogueFilterRow}>
        <label style={styles.catalogueFilterOption}>
          <input style={styles.checkboxInput} type="checkbox" checked={catalogueFilterByIndustry} onChange={(event) => setCatalogueFilterByIndustry(event.target.checked)} />
          <span style={styles.catalogueFilterLabel}>Filter by Industry Type</span>
        </label>
        <label style={styles.catalogueFilterOption}>
          <input style={styles.checkboxInput} type="checkbox" checked={catalogueFilterByEmploymentType} onChange={(event) => setCatalogueFilterByEmploymentType(event.target.checked)} />
          <span style={styles.catalogueFilterLabel}>Filter by Employment Type</span>
        </label>
      </div>
      {renderCatalogueRows("Mandatory", catalogueGroups.mandatory)}
      {renderCatalogueRows("Statutory", catalogueGroups.statutory)}
      {renderCatalogueRows("Variable", catalogueGroups.variable)}
      {renderCatalogueRows("Optional", catalogueGroups.optional)}
      {renderCatalogueRows("Balancing", catalogueGroups.balancing)}
      {renderCatalogueRows("Informational", catalogueGroups.informational)}
    </section>
  );

  const renderStructureEditor = () => (
    <>
      <div style={styles.editorGrid}>
        {renderReadOnlyInput("CTC Structure ID", structureForm.CTCStructureId, true)}
        {renderInput("Structure Name", "StructureName", structureForm.StructureName, handleStructureChange, true)}
        {renderSelect("Country", "CountryCode", structureForm.CountryCode, handleStructureChange, countryOptions, true)}
        {renderInput("Region Group", "RegionGroup", structureForm.RegionGroup, handleStructureChange)}
        {renderInput("Legal Entity Code", "LegalEntityCode", structureForm.LegalEntityCode, handleStructureChange)}
        {renderInput("Location Code", "LocationCode", structureForm.LocationCode, handleStructureChange)}
        {renderInput("Department Code", "DepartmentCode", structureForm.DepartmentCode, handleStructureChange)}
        {renderInput("Work Unit Code", "WorkUnitCode", structureForm.WorkUnitCode, handleStructureChange)}
        {renderSelect("Industry Type", "IndustryType", structureForm.IndustryType, handleStructureChange, INDUSTRY_OPTIONS, true)}
        {renderSelect("Employment Type", "EmploymentType", structureForm.EmploymentType, handleStructureChange, EMPLOYMENT_TYPE_OPTIONS, true)}
        {renderSelect("Work Arrangement", "WorkArrangement", structureForm.WorkArrangement, handleStructureChange, WORK_ARRANGEMENT_OPTIONS)}
        {renderSelect("Pay Frequency", "PayFrequency", structureForm.PayFrequency, handleStructureChange, PAY_FREQUENCY_OPTIONS, true)}
        {renderSelect("Default Currency", "DefaultCurrency", structureForm.DefaultCurrency, handleStructureChange, CURRENCY_OPTIONS, true)}
        {renderSelect("Structure Basis", "StructureBasis", structureForm.StructureBasis, handleStructureChange, ["AnnualCTC", "CTC", "Gross", "HourlyRate", "Consolidated", "Stipend"], true)}
        {renderInput("Effective From", "EffectiveFrom", structureForm.EffectiveFrom, handleStructureChange, true, "date")}
        {renderInput("Effective To", "EffectiveTo", structureForm.EffectiveTo, handleStructureChange, false, "date")}
        {renderSelect("Status", "Status", structureForm.Status, handleStructureChange, ["Draft", "Active", "Retired"], true)}
        {renderInput("Display Order", "DisplayOrder", structureForm.DisplayOrder, handleStructureChange, false, "number")}
        {renderCheckbox("Statutory Structure", "IsStatutoryStructure", structureForm.IsStatutoryStructure, handleStructureChange)}
        {renderCheckbox("Default", "IsDefault", structureForm.IsDefault, handleStructureChange)}
        {renderCheckbox("Active", "IsActive", structureForm.IsActive, handleStructureChange)}
        {renderCheckbox("Requires Approval", "RequiresApproval", structureForm.RequiresApproval, handleStructureChange)}
        {renderCheckbox("Supports Variable Pay", "SupportsVariablePay", structureForm.SupportsVariablePay, handleStructureChange)}
        {renderCheckbox("Supports Hourly Rates", "SupportsHourlyRates", structureForm.SupportsHourlyRates, handleStructureChange)}
        {renderCheckbox("Supports Shift Hours/Allowance", "SupportsShiftAllowance", structureForm.SupportsShiftAllowance, handleStructureChange)}
      </div>
      <div style={{ marginTop: "14px" }}>
        {renderTextarea("Notes", "Notes", structureForm.Notes, handleStructureChange)}
      </div>
    </>
  );

  const renderComponentEditor = () => (
    <>
      <div style={styles.editorGrid}>
        {renderSelect("CTC Structure", "CTCStructureId", componentForm.CTCStructureId, handleStructureSelectForComponent, availableStructures.map((item) => ({ value: item.ctcStructureId, label: `${item.ctcStructureId} - ${item.structureName}` })), true)}
        {renderInput("Component Code", "ComponentCode", componentForm.ComponentCode, handleComponentChange, true)}
        {renderReadOnlyInput("CTC Component ID", componentForm.CTCComponentId, true)}
        {renderInput("Component Name", "ComponentName", componentForm.ComponentName, handleComponentChange, true)}
        {renderSelect("Country", "CountryCode", componentForm.CountryCode, handleComponentChange, countryOptions, true)}
        {renderSelect("Category", "ComponentCategory", componentForm.ComponentCategory, handleComponentChange, COMPONENT_CATEGORY_OPTIONS, true)}
        {renderInput("Sub Category", "ComponentSubCategory", componentForm.ComponentSubCategory, handleComponentChange)}
        {renderSelect("Calculation Method", "CalculationMethod", componentForm.CalculationMethod, handleComponentChange, COMPONENT_CALC_METHOD_OPTIONS, true)}
        {renderSelect("Proration Method", "ProrationMethod", componentForm.ProrationMethod, handleComponentChange, PRORATION_METHOD_OPTIONS)}
        {renderInput("Base Component Code", "CalculationBaseComponentCode", componentForm.CalculationBaseComponentCode, handleComponentChange)}
        {renderInput("Default %", "DefaultPercentage", componentForm.DefaultPercentage, handleComponentChange, false, "number")}
        {renderInput("Default Amount", "DefaultAmount", componentForm.DefaultAmount, handleComponentChange, false, "number")}
        {renderInput("Minimum Amount", "MinimumAmount", componentForm.MinimumAmount, handleComponentChange, false, "number")}
        {renderInput("Maximum Amount", "MaximumAmount", componentForm.MaximumAmount, handleComponentChange, false, "number")}
        {renderSelect("Taxable", "IsTaxable", componentForm.IsTaxable, handleComponentChange, TAXABILITY_OPTIONS)}
        {renderSelect("Applicability Type", "ApplicabilityType", componentForm.ApplicabilityType, handleComponentChange, APPLICABILITY_TYPE_OPTIONS)}
        {renderCheckbox("HR Selectable", "HRSelectable", componentForm.HRSelectable, handleComponentChange)}
        {renderSelect("Display Group", "DisplayGroup", componentForm.DisplayGroup, handleComponentChange, DISPLAY_GROUP_OPTIONS)}
        {renderInput("Display Order", "DisplayOrder", componentForm.DisplayOrder, handleComponentChange, false, "number")}
        {renderInput("Effective From", "EffectiveFrom", componentForm.EffectiveFrom, handleComponentChange, true, "date")}
        {renderInput("Effective To", "EffectiveTo", componentForm.EffectiveTo, handleComponentChange, false, "date")}
        {renderSelect("Status", "Status", componentForm.Status, handleComponentChange, ["Draft", "Active", "Retired"], true)}
{renderCheckbox("Employer Cost", "IsEmployerCost", componentForm.IsEmployerCost, handleComponentChange)}
        {renderCheckbox("Employee Deduction", "IsEmployeeDeduction", componentForm.IsEmployeeDeduction, handleComponentChange)}
        {renderCheckbox("Cash Component", "IsCashComponent", componentForm.IsCashComponent, handleComponentChange)}
        {renderCheckbox("Variable", "IsVariable", componentForm.IsVariable, handleComponentChange)}
        {renderCheckbox("Prorated", "IsProrated", componentForm.IsProrated, handleComponentChange)}
        {renderCheckbox("Include in Annual CTC", "IncludeInAnnualCTC", componentForm.IncludeInAnnualCTC, handleComponentChange)}
        {renderCheckbox("Include in Monthly Gross", "IncludeInMonthlyGross", componentForm.IncludeInMonthlyGross, handleComponentChange)}
        {renderCheckbox("Include in Net Pay Estimate", "IncludeInNetPayEstimate", componentForm.IncludeInNetPayEstimate, handleComponentChange)}
        {renderCheckbox("Active", "IsActive", componentForm.IsActive, handleComponentChange)}
      </div>
      <div style={{ marginTop: "14px" }}>
        {renderTextarea("Notes", "Notes", componentForm.Notes, handleComponentChange)}
      </div>
    </>
  );

  const renderSetupEditor = () => {
    if (!setupEditorOpen) return null;
    const isStructure = setupEditorType === "structure";
    return (
      <section style={styles.editorPanel}>
        <div style={styles.editorTopBar}>
          <div>
            <p style={styles.eyebrow}>Master Data / Add/Edit Editor</p>
            <h2 style={styles.panelTitle}>{setupEditorMode === "add" ? "Add" : setupEditorMode === "view" ? "View" : "Edit"} {isStructure ? "CTC Structure" : "CTC Component"}</h2>
            <p style={styles.panelSubtitle}>{setupEditorMode === "view" || !canEditCTCSetup ? "View-only mode. Use Close to return." : "Controlled selection chain stays unchanged; setup fields are edited in context."}</p>
          </div>
          <button type="button" style={styles.closeButton} onClick={closeSetupEditor}>Close</button>
        </div>
        {isStructure ? renderStructureEditor() : renderComponentEditor()}
        {setupEditorMode !== "view" && canEditCTCSetup && (
          <div style={styles.bottomActions}>
            <button type="button" style={styles.primaryButton} onClick={isStructure ? handleSaveStructure : handleSaveComponent} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
            <button type="button" style={styles.secondaryButton} onClick={cancelSetupEditor} disabled={saving}>Cancel</button>
          </div>
        )}
      </section>
    );
  };

  const renderComponentGroup = (title, rows, description) => (
    <section style={styles.subPanel}>
      <div style={styles.groupHeader}><div><h3 style={styles.groupTitle}>{title}</h3><p style={styles.groupSubtitle}>{description}</p></div><span style={styles.pill}>{rows.length}</span></div>
      <div style={styles.tableWrapper}><table style={styles.compactTable}><thead><tr><th style={styles.th}>Code</th><th style={styles.th}>Component</th><th style={styles.th}>Annual</th><th style={styles.th}>Monthly</th><th style={styles.th}>Impact</th></tr></thead><tbody>
        {rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="5">No components in this group.</td></tr>}
        {rows.map((row) => <tr key={`${title}-${row.componentCode}`}><td style={styles.td}>{displayText(row.componentCode)}</td><td style={styles.td}>{displayText(row.componentName)}</td><td style={styles.td}>{formatMoney(row.annualAmount, draft.SalaryCurrency)}</td><td style={styles.td}>{formatMoney(row.monthlyAmount, draft.SalaryCurrency)}</td><td style={styles.td}>{row.isTaxComponent ? "Tax" : row.isInsuranceOrStatutoryComponent ? "Statutory" : row.isEmployeeDeduction ? "Deduction" : row.isEmployerCost ? "Employer Cost" : "Pay"}</td></tr>)}
      </tbody></table></div>
    </section>
  );
  const renderOptionalComponentsPanel = () => (
    <section style={styles.decisionPanel}>
      <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>Optional Components — HR Selection</h2><p style={styles.panelSubtitle}>Select approved optional components for this employee. Special Allowance / Balancing automatically absorbs the remaining CTC budget.</p></div><span style={styles.pill}>{availableOptionalComponents.length} available</span></div>
      <div style={styles.optionalGrid}>{availableOptionalComponents.length === 0 && <div style={styles.emptyBox}>No HR-selectable optional components are configured under this CTC Structure. Add optional components like Meal/Food, Internet, Mobile, Vehicle, LTA or Wellness benefit if HR needs more flexibility.</div>}
        {availableOptionalComponents.map((component) => { const code = getComponentCode(component); return <label key={code} style={selectedOptionalComponentCodes[code] ? styles.optionalCardSelected : styles.optionalCard}><input type="checkbox" checked={Boolean(selectedOptionalComponentCodes[code])} onChange={(event) => handleOptionalComponentToggle(code, event.target.checked)} /><span><strong>{code}</strong> — {displayText(component.componentName || getComponentValue(component, "ComponentName", "field_4"))}</span><small>{displayText(component.isTaxable || getComponentValue(component, "IsTaxable", "field_14", "NotApplicable"))} · {displayText(component.calculationMethod || getComponentValue(component, "CalculationMethod", "field_8"))}</small></label>; })}
      </div>
    </section>
  );
  const renderDecisionPanel = () => (
    <section style={styles.decisionPanel}>
      <div style={styles.panelHeader}><div><h2 style={styles.panelTitle}>CTC Decision Panel</h2><p style={styles.panelSubtitle}>Compares budget, target take-home, component flexibility, within-budget adjustment, and extra company burden.</p></div></div>
      <div style={styles.decisionGrid}>
        <div style={styles.metricCard}><span>Target Monthly Take Home</span><strong>{formatMoney(decisionPanel.targetMonthly, draft.SalaryCurrency)}</strong></div>
        <div style={styles.metricCard}><span>Current Monthly Take Home</span><strong>{formatMoney(decisionPanel.currentTakeHomeMonthly, draft.SalaryCurrency)}</strong></div>
        <div style={decisionPanel.shortfallAnnual > 0 ? styles.warningMetricCard : styles.metricCard}><span>Annual Shortfall</span><strong>{formatMoney(decisionPanel.shortfallAnnual, draft.SalaryCurrency)}</strong></div>
        <div style={styles.metricCard}><span>Flexible Variable Pool</span><strong>{formatMoney(decisionPanel.variableFlexiblePool, draft.SalaryCurrency)}</strong></div>
        <div style={decisionPanel.canAdjustWithinBudget ? styles.goodMetricCard : styles.metricCard}><span>Can Adjust Within Budget?</span><strong>{decisionPanel.targetMonthly ? (decisionPanel.canAdjustWithinBudget || decisionPanel.shortfallAnnual <= 0 ? "Yes" : "No") : "-"}</strong></div>
        <div style={decisionPanel.extraCTCRequired > 0 ? styles.warningMetricCard : styles.metricCard}><span>Extra Company Burden</span><strong>{formatMoney(decisionPanel.extraCTCRequired, draft.SalaryCurrency)}</strong></div>
      </div>
      <div style={styles.recommendationBox}>{decisionPanel.recommendation}</div>
      {componentGroups.variable.filter((row) => row.hrSelectable).length > 0 && (
        <div style={styles.variableAdjustBox}>
          <div style={styles.groupHeader}>
            <div>
              <h3 style={styles.groupTitle}>Flexible Variable Adjustment</h3>
              <p style={styles.groupSubtitle}>HR can reduce HR-selectable variable pay and let Special Allowance / Balancing absorb the released CTC budget.</p>
            </div>
            <button type="button" style={styles.secondaryButton} onClick={applySuggestedVariableReallocation} disabled={!decisionPanel.shortfallAnnual}>Apply Suggested Reallocation</button>
          </div>
          <div style={styles.variableAdjustGrid}>
            {componentGroups.variable.filter((row) => row.hrSelectable).map((row) => (
              <label key={`var-adjust-${row.componentCode}`} style={styles.field}>
                <span style={styles.label}>{row.componentCode} — {row.componentName}</span>
                <input
                  type="number"
                  style={styles.input}
                  value={manualComponentOverrides[row.componentCode] ?? row.annualAmount ?? ""}
                  onChange={(event) => handleManualComponentAmountChange(row.componentCode, event.target.value)}
                  placeholder="Annual variable amount"
                />
                <span style={styles.helperText}>Current annual variable target: {formatMoney(row.annualAmount, draft.SalaryCurrency)}. Reduce this amount to improve monthly take-home within the same CTC.</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {decisionPanel.shortfallAnnual > 0 && !decisionPanel.canAdjustWithinBudget && <div style={styles.suggestionBox}><strong>Suggested optional components to add/configure for HR flexibility:</strong><div style={styles.suggestionList}>{decisionPanel.suggestedNewComponents.map((item) => <span key={item} style={styles.suggestionPill}>{item}</span>)}</div><p style={styles.panelSubtitle}>Suggestions must be validated by HR/payroll under country and unit rules.</p></div>}
    </section>
  );

  const renderApprovalMetric = (label, value, currency = selectedPending?.salaryCurrency) => (
    <div style={styles.metricCard}><span>{label}</span><strong>{typeof value === "number" ? formatMoney(value, currency) : displayText(value)}</strong></div>
  );

  const renderApprovalComponentGroup = (title, rows, description) => (
    <section style={styles.subPanel}>
      <div style={styles.groupHeader}><div><h3 style={styles.groupTitle}>{title}</h3><p style={styles.groupSubtitle}>{description}</p></div><span style={styles.pill}>{rows.length}</span></div>
      <div style={styles.tableWrapper}>
        <table style={styles.compactTable}>
          <thead><tr><th style={styles.th}>Code</th><th style={styles.th}>Component</th><th style={styles.th}>Annual</th><th style={styles.th}>Monthly</th><th style={styles.th}>Type</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="5">No components in this group.</td></tr>}
            {rows.map((row) => (
              <tr key={`${title}-${row.componentCode || row.ComponentCode}`}>
                <td style={styles.td}>{displayText(row.componentCode || row.ComponentCode)}</td>
                <td style={styles.td}>{displayText(row.componentName || row.ComponentName)}</td>
                <td style={styles.td}>{formatMoney(row.annualAmount || row.AnnualAmount, selectedPending?.salaryCurrency)}</td>
                <td style={styles.td}>{formatMoney(row.monthlyAmount || row.MonthlyAmount, selectedPending?.salaryCurrency)}</td>
                <td style={styles.td}>{displayText(row.applicabilityType || row.ApplicabilityType || (row.isEmployeeDeduction ? "Deduction" : row.isEmployerCost ? "Employer Cost" : "Pay"))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderApprovalDetailItem = (label, value, extra = null) => (
    <div style={styles.approvalDetailItem}>
      <span style={styles.approvalDetailLabel}>{label}</span>
      <strong style={styles.approvalDetailValue}>{displayText(value)}</strong>
      {extra ? <small style={styles.approvalDetailSubValue}>{extra}</small> : null}
    </div>
  );

  const renderApprovalReviewPanel = () => {
    if (!selectedPending) {
      return <div style={styles.emptyBox}>Select a pending approval row to review CTC details before approval or rejection.</div>;
    }
    return (
      <section style={styles.approvalReviewPanel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Approval Review — Employee CTC Details</h2>
            <p style={styles.panelSubtitle}>Controlled selection chain, submitted compensation parameters, component breakup and revision context for approver review.</p>
          </div>
          <span style={styles.statusPill}>{displayText(selectedPending.ctcStatus, "PendingApproval")}</span>
        </div>
        <div style={styles.approvalSection}>
          <h3 style={styles.groupTitle}>Controlled Selection Chain</h3>
          <div style={styles.detailGrid}>
            {renderApprovalDetailItem("Country", selectedPending.countryCode)}
            {renderApprovalDetailItem("Legal Entity", selectedPending.legalEntityCode)}
            {renderApprovalDetailItem("Location", selectedPending.locationCode)}
            {renderApprovalDetailItem("Department", selectedPending.departmentCode)}
            {renderApprovalDetailItem("Work Unit", selectedPending.workUnitCode)}
            {renderApprovalDetailItem("Industry", selectedPending.industryType)}
            {renderApprovalDetailItem("Employment Type", selectedPending.employmentType)}
            {renderApprovalDetailItem("Work Arrangement", selectedPending.workArrangement)}
          </div>
        </div>
        <div style={styles.approvalSection}>
          <h3 style={styles.groupTitle}>Submission / Revision Context</h3>
          <div style={styles.detailGrid}>
            {renderApprovalDetailItem("Employee", `${displayText(selectedPending.employeeCode)} — ${displayText(selectedPending.employeeName)}`)}
            {renderApprovalDetailItem("Employee CTC ID", selectedPending.employeeCTCId)}
            {renderApprovalDetailItem("CTC Structure", selectedPending.ctcStructureId, displayText(selectedPendingStructure?.structureName, ""))}
            {renderApprovalDetailItem("Pay Grade", selectedPending.payGrade)}
            {renderApprovalDetailItem("Revision No.", selectedPending.revisionNumber)}
            {renderApprovalDetailItem("Previous CTC ID", selectedPending.previousEmployeeCTCId)}
            {renderApprovalDetailItem("Submitted By", selectedPending.submittedBy)}
            {renderApprovalDetailItem("Submitted On", formatDate(selectedPending.submittedOn))}
            {renderApprovalDetailItem("Effective From", formatDate(selectedPending.effectiveFrom))}
            {renderApprovalDetailItem("Effective To", formatDate(selectedPending.effectiveTo))}
          </div>
          <div style={styles.reviewNotesGrid}>
            <div style={styles.noteBox}><span style={styles.approvalDetailLabel}>HR Revision Reason</span><p style={styles.noteText}>{displayText(selectedPending.revisionReason, "No revision reason provided.")}</p></div>
            <div style={styles.noteBox}><span style={styles.approvalDetailLabel}>HR Remarks</span><p style={styles.noteText}>{displayText(selectedPending.remarks, "No HR remarks provided.")}</p></div>
          </div>
        </div>
        <div style={styles.ctcCardGrid}>
          {renderApprovalMetric("Annual CTC", selectedPendingTotals.annualCTC)}
          {renderApprovalMetric("Monthly CTC", selectedPendingTotals.monthlyCTC)}
          {renderApprovalMetric("Annual Gross", selectedPendingTotals.annualGross)}
          {renderApprovalMetric("Monthly Gross", selectedPendingTotals.monthlyGross)}
          {renderApprovalMetric("Employer Cost", selectedPendingTotals.annualEmployerCost)}
          {renderApprovalMetric("Employee Deductions", selectedPendingTotals.annualEmployeeDeductions)}
          {renderApprovalMetric("Variable Pay Target", selectedPendingTotals.annualVariablePayTarget)}
          {renderApprovalMetric("Monthly Take Home Estimate", selectedPendingTotals.netPayMonthly)}
        </div>
        <div style={styles.componentGroupGrid}>
          {renderApprovalComponentGroup("Mandatory Components", selectedPendingGroups.mandatory, "Always included components.")}
          {renderApprovalComponentGroup("Statutory / Tax Components", selectedPendingGroups.statutory, "Statutory, tax, insurance and employee deduction components.")}
          {renderApprovalComponentGroup("Optional Components", selectedPendingGroups.optional, "HR-selected optional components submitted for this employee.")}
          {renderApprovalComponentGroup("Variable Components", selectedPendingGroups.variable, "Variable/flexible pay components submitted for approval.")}
          {renderApprovalComponentGroup("Balancing Components", selectedPendingGroups.balancing, "Derived balancing components such as Special Allowance.")}
        </div>
        <label style={{ ...styles.field, marginTop: "14px" }}>
          <span style={styles.label}>Approver Remarks / Review Notes</span>
          <textarea style={styles.textareaSmall} value={approvalRemarks} onChange={(event) => setApprovalRemarks(event.target.value)} placeholder="Approver remarks for approval, revision instruction, or rejection context" />
        </label>
      </section>
    );
  };

  const messageStyle = messageType === "error" ? styles.errorBox : styles.successBox;

  return (
    <div className="fm-hr-page-shell" style={styles.page}>
      <div className="fm-hr-hero" style={styles.header}>
        <div>
          <p style={styles.eyebrow}>HR Module / Compensation</p>
          <h1 style={styles.title}>CTC Console</h1>
          <p style={styles.subtitle}>Compensation-owned setup and employee CTC actions with controlled HR Settings master-data dependency chain.</p>
        </div>
        <div style={styles.headerActions}>
          <div style={styles.roleBadge}>Current role: {currentUserRole}</div>
          <button type="button" onClick={loadInitialData} style={styles.secondaryButton} disabled={loading || saving}>{loading ? "Loading..." : "Refresh"}</button>
          <button type="button" onClick={handleDataQuality} style={styles.secondaryButton} disabled={loading || saving}>Data Quality</button>
        </div>
      </div>

      {message && <div style={messageStyle}>{message}</div>}

      <div style={styles.tabBar}>
        <button type="button" style={activeTab === "setup" ? styles.tabActive : styles.tab} onClick={() => setActiveTab("setup")}>CTC Setup</button>
        <button type="button" style={activeTab === "employee" ? styles.tabActive : styles.tab} onClick={() => setActiveTab("employee")}>Employee CTC</button>
        <button type="button" style={activeTab === "approvals" ? styles.tabActive : styles.tab} onClick={() => setActiveTab("approvals")}>Approvals</button>
      </div>

      <section style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={styles.panelTitle}>Controlled Selection Chain</h2>
            <p style={styles.panelSubtitle}>Country → Legal Entity → Location → Department → Work Unit → CTC Structure → Components. Users cannot type these master values manually.</p>
          </div>
        </div>
        <div style={styles.editorGrid}>
          {renderSelect("Country", "CountryCode", orgFilter.CountryCode, handleOrgFilterChange, countryOptions, true)}
          {renderSelect("Legal Entity", "LegalEntityCode", orgFilter.LegalEntityCode, handleOrgFilterChange, legalEntityOptions, true)}
          {renderSelect("Location", "LocationCode", orgFilter.LocationCode, handleOrgFilterChange, locationOptions)}
          {renderSelect("Department", "DepartmentCode", orgFilter.DepartmentCode, handleOrgFilterChange, departmentOptions)}
          {renderSelect("Work Unit", "WorkUnitCode", orgFilter.WorkUnitCode, handleOrgFilterChange, workUnitOptions)}
          {renderSelect("Industry Type", "IndustryType", orgFilter.IndustryType, handleOrgFilterChange, INDUSTRY_OPTIONS, true)}
          {renderSelect("Employment Type", "EmploymentType", orgFilter.EmploymentType, handleOrgFilterChange, EMPLOYMENT_TYPE_OPTIONS, true)}
          {renderSelect("Work Arrangement", "WorkArrangement", orgFilter.WorkArrangement, handleOrgFilterChange, WORK_ARRANGEMENT_OPTIONS)}
        </div>
      </section>

      <section style={styles.summaryGrid}>
        <div style={styles.summaryCard}><strong>Available Structures</strong><span style={styles.badge}>{availableStructures.length}</span><p>Filtered by full controlled chain</p></div>
        <div style={styles.summaryCard}><strong>Available Components</strong><span style={styles.badge}>{selectedStructureComponents.length}</span><p>Filtered by selected CTC structure</p></div>
        <div style={styles.summaryCard}><strong>Pending Approvals</strong><span style={styles.badge}>{pendingApprovals.length}</span><p>CTC drafts waiting for approval</p></div>
      </section>

      {activeTab === "setup" && (
        <>
          {renderPayScaleBandPanel()}
          {payScaleEditorOpen && renderPayScaleBandEditor()}

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>CTC Structures</h2>
                <p style={styles.panelSubtitle}>List: HR_CTCStructures · Add or edit structures after selecting country/legal entity/location/department/work unit above.</p>
              </div>
              <div style={styles.inlineActions}>
                <button type="button" style={styles.primaryButton} onClick={openAddStructure} disabled={!canEditCTCSetup}>Add</button>
                <button type="button" style={styles.secondaryButton} onClick={() => openEditStructure(selectedStructure, "edit")} disabled={!selectedStructureId || !canEditCTCSetup}>Edit Selected</button>
              </div>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Select</th>
                    <th style={styles.th}>Structure ID</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Country</th>
                    <th style={styles.th}>Legal Entity</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.th}>Department</th>
                    <th style={styles.th}>Work Unit</th>
                    <th style={styles.th}>Industry</th>
                    <th style={styles.th}>Employment</th>
                    <th style={styles.th}>Work Arrangement</th>
                    <th style={styles.th}>Currency</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Active</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {availableStructures.length === 0 && <tr><td style={styles.emptyCell} colSpan="15">No CTC Structures found for current filters. Click Add to create one.</td></tr>}
                  {availableStructures.map((row) => (
                    <tr key={row.ctcStructureId || row.id}>
                      <td style={styles.td}><input type="radio" name="selectedCTCStructure" checked={selectedStructureId === row.ctcStructureId} onChange={() => { setSelectedStructureId(row.ctcStructureId); setSelectedComponentId(""); setComponentForm((prev) => ({ ...prev, CTCStructureId: row.ctcStructureId, CountryCode: row.countryCode || orgFilter.CountryCode })); }} /></td>
                      <td style={styles.td}>{displayText(row.ctcStructureId)}</td>
                      <td style={styles.td}>{displayText(row.structureName)}</td>
                      <td style={styles.td}>{displayText(row.countryCode)}</td>
                      <td style={styles.td}>{displayText(row.legalEntityCode, "ALL")}</td>
                      <td style={styles.td}>{displayText(row.locationCode, "ALL")}</td>
                      <td style={styles.td}>{displayText(row.departmentCode, "ALL")}</td>
                      <td style={styles.td}>{displayText(row.workUnitCode, "ALL")}</td>
                      <td style={styles.td}>{displayText(row.industryType)}</td>
                      <td style={styles.td}>{displayText(row.employmentType)}</td>
                      <td style={styles.td}>{displayText(row.workArrangement, "ALL")}</td>
                      <td style={styles.td}>{displayText(row.defaultCurrency)}</td>
                      <td style={styles.td}>{displayText(row.status)}</td>
                      <td style={styles.td}>{row.isActive ? "Yes" : "No"}</td>
                      <td style={styles.td}><button type="button" style={styles.rowButton} onClick={() => { setSelectedComponentId(""); openEditStructure(row, "view"); }}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {setupEditorOpen && setupEditorType === "structure" && renderSetupEditor()}

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>CTC Components</h2>
                <p style={styles.panelSubtitle}>List: HR_CTCComponents · Multiple components can exist under one structure, but ComponentCode must be unique within that structure.</p>
              </div>
              <div style={styles.inlineActions}>
                <button type="button" style={styles.primaryButton} onClick={openAddComponent} disabled={!canEditCTCSetup}>Add</button>
                <button type="button" style={styles.secondaryButton} onClick={() => openEditComponent(selectedComponent, "edit")} disabled={!selectedComponentId || !canEditCTCSetup}>Edit Selected</button>
              </div>
            </div>
            <div style={styles.helperBox}>
              Selected structure components: {selectedStructureComponents.length ? selectedStructureComponents.map((item) => item.componentCode).filter(Boolean).join(", ") : "None selected / no components yet."}
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Select</th>
                    <th style={styles.th}>Component ID</th>
                    <th style={styles.th}>Structure ID</th>
                    <th style={styles.th}>Code</th>
                    <th style={styles.th}>Name</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Applicability</th>
                    <th style={styles.th}>HR Selectable</th>
                    <th style={styles.th}>Method</th>
                    <th style={styles.th}>Order</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStructureComponents.length === 0 && <tr><td style={styles.emptyCell} colSpan="12">No CTC Components found for selected/current structure. Click Add to create one.</td></tr>}
                  {selectedStructureComponents.map((row) => (
                    <tr key={row.ctcComponentId || row.id}>
                      <td style={styles.td}><input type="radio" name="selectedCTCComponent" checked={selectedComponentId === row.ctcComponentId} onChange={() => setSelectedComponentId(row.ctcComponentId)} /></td>
                      <td style={styles.td}>{displayText(row.ctcComponentId)}</td>
                      <td style={styles.td}>{displayText(row.ctcStructureId)}</td>
                      <td style={styles.td}>{displayText(row.componentCode)}</td>
                      <td style={styles.td}>{displayText(row.componentName)}</td>
                      <td style={styles.td}>{displayText(row.componentCategory)}</td>
                      <td style={styles.td}>{displayText(row.applicabilityType || getApplicabilityType(row))}</td>
                      <td style={styles.td}>{isHRSelectableComponent(row) ? "Yes" : "No"}</td>
                      <td style={styles.td}>{displayText(row.calculationMethod)}</td>
                      <td style={styles.td}>{displayText(row.displayOrder)}</td>
                      <td style={styles.td}>{displayText(row.status)}</td>
                      <td style={styles.td}><button type="button" style={styles.rowButton} onClick={() => openEditComponent(row, "view")}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {renderComponentCataloguePanel()}

          {setupEditorOpen && setupEditorType === "component" && renderSetupEditor()}
        </>
      )}
      {activeTab === "employee" && (
        <>
          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Employee Current CTC</h2>
                {currentPayScaleValidation && (
                  <div style={currentPayScaleValidation.valid ? styles.successBox : styles.warningBox}>
                    Pay scale validation: {currentPayScaleValidation.message}
                  </div>
                )}
                <p style={styles.panelSubtitle}>Load approved current CTC and history by Employee Code.</p>
              </div>
              <div style={styles.inlineActions}>
                <input style={styles.input} value={employeeCode} onChange={(event) => setEmployeeCode(event.target.value)} placeholder="Employee Code e.g. EMP001" />
                <button type="button" style={styles.primaryButton} onClick={() => loadEmployeeCTC()} disabled={loading || saving}>Load CTC</button>
                <button type="button" style={styles.secondaryButton} onClick={() => setShowHistory(true)} disabled={!historyRows.length}>History</button>
              </div>
            </div>
            {currentCTC ? (
              <div style={styles.ctcCardGrid}>
                <div style={styles.metricCard}><span>Employee CTC ID</span><strong>{displayText(currentCTC.employeeCTCId)}</strong></div>
                <div style={styles.metricCard}><span>Status</span><strong>{displayText(currentCTC.ctcStatus)}{currentCTC.revisionDueFlag ? " / Revision Due" : ""}</strong></div>
                <div style={styles.metricCard}><span>Annual CTC</span><strong>{formatMoney(currentCTC.annualCTC, currentCTC.salaryCurrency)}</strong></div>
                <div style={styles.metricCard}><span>Monthly Gross</span><strong>{formatMoney(currentCTC.monthlyGross, currentCTC.salaryCurrency)}</strong></div>
                <div style={styles.metricCard}><span>Employer Cost</span><strong>{formatMoney(currentCTC.annualEmployerCost, currentCTC.salaryCurrency)}</strong></div>
                <div style={styles.metricCard}><span>Effective From</span><strong>{formatDate(currentCTC.effectiveFrom)}</strong></div>
              </div>
            ) : (
              <div style={styles.emptyBox}>No current approved CTC loaded.</div>
            )}
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Eligible Employees for CTC Fixation / Revision</h2>
                <p style={styles.panelSubtitle}>Employees are filtered from EmployeeMaster by the controlled selection chain and Compensation Status: Not Started, Pending Entry, Revision Due, or Correction Required.</p>
              </div>
              <button type="button" style={styles.secondaryButton} onClick={loadInitialData} disabled={loading || saving}>Refresh Employees</button>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Action</th>
                    <th style={styles.th}>Employee</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Country</th>
                    <th style={styles.th}>Legal Entity</th>
                    <th style={styles.th}>Location</th>
                    <th style={styles.th}>Department</th>
                    <th style={styles.th}>Work Unit</th>
                    <th style={styles.th}>Employment</th>
                    <th style={styles.th}>Pay Grade</th>
                    <th style={styles.th}>Comp Status</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibleEmployees.length === 0 && <tr><td style={styles.emptyCell} colSpan="11">No eligible employees found for current selection chain. Add Active dummy employees in EmployeeMaster with Compensation Status Pending Entry / Revision Due.</td></tr>}
                  {eligibleEmployees.map((employee) => (
                    <tr key={employee.employeeCode} style={selectedEmployeeCode === employee.employeeCode ? styles.selectedRow : undefined}>
                      <td style={styles.td}><button type="button" style={styles.rowButton} onClick={() => handleSelectEligibleEmployee(employee)}>Select</button></td>
                      <td style={styles.td}>{employee.employeeCode}<div style={styles.mutedText}>{employee.employeeName}</div></td>
                      <td style={styles.td}>{employee.workEmail}</td>
                      <td style={styles.td}>{employee.countryCode}</td>
                      <td style={styles.td}>{employee.legalEntityCode}</td>
                      <td style={styles.td}>{employee.locationCode}</td>
                      <td style={styles.td}>{employee.departmentCode}</td>
                      <td style={styles.td}>{employee.workUnitCode}</td>
                      <td style={styles.td}>{employee.employmentType}</td>
                      <td style={styles.td}>{employee.payGrade}</td>
                      <td style={styles.td}>{employee.compensationStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Create Employee CTC Draft</h2>
                <p style={styles.panelSubtitle}>Select employee + CTC Structure, enter Annual CTC and Effective From. Component breakup and totals are calculated automatically from CTC Components.</p>
              </div>

            </div>
            <div style={styles.editorGrid}>
              {renderReadOnlyInput("Employee CTC ID", draft.EmployeeCTCId, true)}
              {renderReadOnlyInput("Employee Code", draft.EmployeeCode, true)}
              {renderReadOnlyInput("Work Email", draft.WorkEmail)}
              {renderReadOnlyInput("Employee Name", draft.EmployeeName)}
              {renderReadOnlyInput("Pay Grade", draft.PayGrade)}
              {renderSelect("CTC Structure", "CTCStructureId", draft.CTCStructureId, handleDraftChange, availableStructures.map((item) => ({ value: item.ctcStructureId, label: `${item.ctcStructureId} - ${item.structureName}` })), true)}
              {renderSelect("Pay Frequency", "PayFrequency", draft.PayFrequency, handleDraftChange, PAY_FREQUENCY_OPTIONS, true)}
              {renderSelect("Currency", "SalaryCurrency", draft.SalaryCurrency, handleDraftChange, CURRENCY_OPTIONS, true)}
              {renderInput("Annual CTC / Budget", "AnnualCTC", draft.AnnualCTC, handleDraftChange, false, "number")}
              {renderInput("Target Monthly Take Home", "TargetMonthlyNetPay", draft.TargetMonthlyNetPay, handleDraftChange, false, "number")}
              {renderReadOnlyInput("Monthly CTC", draft.MonthlyCTC)}
              {renderReadOnlyInput("Annual Gross", draft.AnnualGross)}
              {renderReadOnlyInput("Monthly Gross", draft.MonthlyGross)}
              {renderReadOnlyInput("Annual Employer Cost", draft.AnnualEmployerCost)}
              {renderReadOnlyInput("Annual Employee Deductions", draft.AnnualEmployeeDeductions)}
              {renderReadOnlyInput("Net Pay Estimate Monthly", draft.NetPayEstimateMonthly)}
              {renderInput("Effective From", "EffectiveFrom", draft.EffectiveFrom, handleDraftChange, true, "date")}
              {renderInput("Effective To", "EffectiveTo", draft.EffectiveTo, handleDraftChange, false, "date", handleDraftDateFocus)}
              {renderReadOnlyInput("Revision Number", draft.RevisionNumber)}
              {renderInput("Revision Reason", "RevisionReason", draft.RevisionReason, handleDraftChange)}
              {renderInput("Remarks", "Remarks", draft.Remarks, handleDraftChange)}
            </div>
            <div style={styles.employeeDraftBottomActions}>
              <button type="button" style={styles.secondaryButton} onClick={recalculateDraftFromComponents} disabled={saving || !draft.CTCStructureId}>Recalculate</button>
              <button type="button" style={styles.primaryButton} onClick={handleSaveDraft} disabled={saving}>Save Draft</button>
              <button type="button" style={styles.secondaryButton} onClick={handleSubmit} disabled={saving}>Submit</button>
              <button type="button" style={styles.dangerButton} onClick={handleCancelDraft} disabled={saving}>Cancel Draft</button>
            </div>

                        {renderOptionalComponentsPanel()}
            {renderDecisionPanel()}
            <div style={styles.componentGroupGrid}>
              {renderComponentGroup("Mandatory Components", componentGroups.mandatory, "Always included for this structure.")}
              {renderComponentGroup("Statutory / Tax Components", componentGroups.statutory, "Country statutory, insurance and tax deductions/contributions.")}
              {renderComponentGroup("Variable Components", componentGroups.variable, "Target/bonus components, normally not monthly take-home.")}
              {renderComponentGroup("Balancing Components", componentGroups.balancing, "Derived residual components such as Special Allowance.")}
            </div>
            <div style={styles.helperBox}>
Selected components: {includedStructureComponents.length} of {selectedStructureComponents.length}. Monthly Net Pay Estimate is the estimated monthly take-home. Variable/bonus target is shown separately and is not included in monthly take-home unless the component is configured for net pay. Manual components such as TDS can be entered in the Manual Annual Amount column below.  
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Code</th>
                    <th style={styles.th}>Component</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Applicability</th>
                    <th style={styles.th}>HR Selectable</th>
                    <th style={styles.th}>Method</th>
                    <th style={styles.th}>Base</th>
                    <th style={styles.th}>%</th>
                    <th style={styles.th}>Manual Annual Amount</th>
                    <th style={styles.th}>Annual</th>
                    <th style={styles.th}>Monthly</th>
                    <th style={styles.th}>Taxable</th>
                    <th style={styles.th}>Employee Deduction</th>
                    <th style={styles.th}>Tax</th>
                    <th style={styles.th}>Statutory / Insurance</th>
                    <th style={styles.th}>Employer Cost</th>
                    <th style={styles.th}>Manual Override</th>
                  </tr>
                </thead>
                <tbody>
                  {calculatedBreakup.rows.length === 0 && <tr><td style={styles.emptyCell} colSpan="17">Select a CTC Structure with active components to calculate breakup.</td></tr>}
                  {calculatedBreakup.rows.map((row) => (
                    <tr key={displayText(row.componentCode)}>
                      <td style={styles.td}>{displayText(row.componentCode)}</td>
                      <td style={styles.td}>{displayText(row.componentName)}</td>
                      <td style={styles.td}>{displayText(row.componentCategory)}</td>
                      <td style={styles.td}>{displayText(row.applicabilityType)}</td>
                      <td style={styles.td}>{row.hrSelectable ? "Yes" : "No"}</td>
                      <td style={styles.td}>{displayText(row.calculationMethod)}</td>
                      <td style={styles.td}>{displayText(row.calculationBaseComponentCode)}</td>
                      <td style={styles.td}>{displayText(row.defaultPercentage)}</td>
                      <td style={styles.td}>
                        {row.manualOverrideAllowed ? (
                          <input
                            type="number"
                            style={styles.compactInput}
                            value={manualComponentOverrides[row.componentCode] ?? row.annualAmount ?? ""}
                            onChange={(event) => handleManualComponentAmountChange(row.componentCode, event.target.value)}
                            placeholder="Annual amount"
                          />
                        ) : "Auto"}
                      </td>
                      <td style={styles.td}>{formatMoney(row.annualAmount, draft.SalaryCurrency)}</td>
                      <td style={styles.td}>{formatMoney(row.monthlyAmount, draft.SalaryCurrency)}</td>
                      <td style={styles.td}>{displayText(row.isTaxable)}</td>
                      <td style={styles.td}>{row.isEmployeeDeduction ? "Yes" : "No"}</td>
                      <td style={styles.td}>{row.isTaxComponent ? "Yes" : "No"}</td>
                      <td style={styles.td}>{row.isInsuranceOrStatutoryComponent ? "Yes" : "No"}</td>
                      <td style={styles.td}>{row.isEmployerCost ? "Yes" : "No"}</td>
                      <td style={styles.td}>{row.manualOverrideAllowed ? "Manual" : "Auto"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={styles.ctcCardGrid}>
              <div style={styles.metricCard}><span>Annual CTC / Budget</span><strong>{formatMoney(calculatedBreakup.totals.AnnualCTC, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Component CTC Total</span><strong>{formatMoney(calculatedBreakup.totals.AnnualCTCComponentTotal, draft.SalaryCurrency)}</strong></div>
              <div style={calculatedBreakup.totals.AnnualCTCDifference < 0 ? styles.warningMetricCard : styles.metricCard}><span>CTC Balance / Overrun</span><strong>{formatMoney(calculatedBreakup.totals.AnnualCTCDifference, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Fixed Gross Pay</span><strong>{formatMoney(calculatedBreakup.totals.AnnualGross, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Variable / Bonus Target</span><strong>{formatMoney(calculatedBreakup.totals.AnnualVariablePayTarget, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Annual Gross incl. Variable</span><strong>{formatMoney(calculatedBreakup.totals.AnnualGrossIncludingVariable, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Employer Cost</span><strong>{formatMoney(calculatedBreakup.totals.AnnualEmployerCost, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Tax Deductions</span><strong>{formatMoney(calculatedBreakup.totals.AnnualTaxDeductions, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Statutory / Insurance Deductions</span><strong>{formatMoney(calculatedBreakup.totals.AnnualStatutoryDeductions, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Other Employee Deductions</span><strong>{formatMoney(calculatedBreakup.totals.AnnualOtherEmployeeDeductions, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Annual Take Home Estimate</span><strong>{formatMoney(calculatedBreakup.totals.AnnualNetPayEstimate, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Monthly Net Pay / Take Home</span><strong>{formatMoney(calculatedBreakup.totals.NetPayEstimateMonthly, draft.SalaryCurrency)}</strong></div>
              <div style={styles.metricCard}><span>Company Cost Estimate</span><strong>{formatMoney(calculatedBreakup.totals.AnnualCompanyCost, draft.SalaryCurrency)}</strong></div>
            </div>

            <details style={{ marginTop: "14px" }}>
              <summary style={styles.label}>System-generated ComponentBreakupJson</summary>
              <textarea name="ComponentBreakupJson" value={draft.ComponentBreakupJson} readOnly style={styles.textarea} />
            </details>
          </section>

          <section style={styles.panel}>
            <div style={styles.panelHeader}>
              <div>
                <h2 style={styles.panelTitle}>Revision Due</h2>
                <p style={styles.panelSubtitle}>Marks RevisionDueFlag true while keeping approved/current/active CTC intact.</p>
              </div>
              <button type="button" style={styles.secondaryButton} onClick={handleMarkRevisionDue} disabled={saving || !(employeeCode || currentCTC?.employeeCode || draft.EmployeeCode)}>Mark Revision Due</button>
            </div>
          </section>
        </>
      )}

      {activeTab === "approvals" && (
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Pending CTC Approvals</h2>
              <p style={styles.panelSubtitle}>Select one pending row to approve, send back for correction, or reject.</p>
            </div>
            <div style={styles.inlineActions}>
              <button type="button" style={styles.primaryButton} onClick={handleApprove} disabled={saving || !selectedPendingId || !canApproveCTC}>Approve</button>
              <button type="button" style={styles.secondaryButton} onClick={handleSendBackForCorrection} disabled={saving || !selectedPendingId || !canApproveCTC}>Send Back for Correction</button>
              <button type="button" style={styles.dangerButton} onClick={handleReject} disabled={saving || !selectedPendingId || !canApproveCTC}>Reject</button>
            </div>
          </div>
          <textarea style={styles.textareaSmall} value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="Correction / rejection reason. Required for Send Back for Correction and Reject." />
          {renderApprovalReviewPanel()}
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Employee CTC ID</th>
                  <th style={styles.th}>Annual CTC</th>
                  <th style={styles.th}>Monthly Gross</th>
                  <th style={styles.th}>Submitted</th>
                  <th style={styles.th}>Effective From</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.length === 0 && <tr><td style={styles.emptyCell} colSpan="7">No pending approvals found.</td></tr>}
                {pendingApprovals.map((row) => (
                  <tr key={row.employeeCTCId || row.id}>
                    <td style={styles.td}><input type="radio" name="pendingCTC" checked={selectedPendingId === row.employeeCTCId} onChange={() => { setSelectedPendingId(row.employeeCTCId); setApprovalRemarks(""); setRejectionReason(""); }} /></td>
                    <td style={styles.td}>{row.employeeCode}<div style={styles.mutedText}>{row.employeeName}</div></td>
                    <td style={styles.td}>{row.employeeCTCId}</td>
                    <td style={styles.td}>{formatMoney(row.annualCTC, row.salaryCurrency)}</td>
                    <td style={styles.td}>{formatMoney(row.monthlyGross, row.salaryCurrency)}</td>
                    <td style={styles.td}>{formatDate(row.submittedOn)}</td>
                    <td style={styles.td}>{formatDate(row.effectiveFrom)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {qualityData && (
        <section style={styles.panel}>
          <h2 style={styles.panelTitle}>Data Quality Source Rows</h2>
          <p style={styles.panelSubtitle}>{qualityData.message}</p>
          <div style={styles.ctcCardGrid}>
            <div style={styles.metricCard}><span>Current Approved Rows</span><strong>{Array.isArray(qualityData.currentApprovedRows) ? qualityData.currentApprovedRows.length : 0}</strong></div>
            <div style={styles.metricCard}><span>Default Structures</span><strong>{Array.isArray(qualityData.defaultStructureRows) ? qualityData.defaultStructureRows.length : 0}</strong></div>
          </div>
        </section>
      )}

      <EmployeeCTCHistoryPanel employeeCode={employeeCode || currentCTC?.employeeCode || draft.EmployeeCode} history={historyRows} open={showHistory} onClose={() => setShowHistory(false)} />
    </div>
  );
}

function renderReadOnlyInput(label, value, required = false) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}{required ? " *" : ""}</span>
      <input value={displayText(value, "")} readOnly style={{ ...styles.input, background: "#e2e8f0", fontWeight: 800 }} />
    </label>
  );
}

function renderTextarea(label, name, value, onChange, required = false) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}{required ? " *" : ""}</span>
      <textarea name={name} value={displayText(value, "")} onChange={onChange} style={styles.textareaSmall} />
    </label>
  );
}

function renderInput(label, name, value, onChange, required = false, type = "text", onFocus = null) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}{required ? " *" : ""}</span>
      <input name={name} type={type} value={displayText(value, "")} onChange={onChange}
        onFocus={onFocus || undefined} style={styles.input} />
    </label>
  );
}

function renderSelect(label, name, value, onChange, options = [], required = false) {
  const normalized = options.map((item) => typeof item === "string" ? { value: item, label: item } : {
    ...item,
    value: displayText(item?.value, ""),
    label: displayText(item?.label ?? item?.value, ""),
  });
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}{required ? " *" : ""}</span>
      <select name={name} value={displayText(value, "")} onChange={onChange} style={styles.select}>
        <option value="">Select</option>
        {normalized.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
      </select>
    </label>
  );
}

function renderCheckbox(label, name, value, onChange) {
  return (
    <label style={styles.checkboxField}>
      <input
        name={name}
        type="checkbox"
        checked={Boolean(value)}
        onChange={onChange}
        style={styles.checkboxInput}
      />
      <span style={styles.checkboxLabel}>{label}</span>
    </label>
  );
}

const styles = {
  page: { fontFamily: "Segoe UI, Calibri, Arial, sans-serif", padding: "18px", background: "var(--fm-content)", minHeight: "100vh", color: "var(--fm-text)", boxSizing: "border-box", overflowX: "hidden" },
  header: { display: "flex", justifyContent: "space-between", gap: "18px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-start", background: "var(--fm-hero-bg)", color: "#ffffff", borderRadius: "20px", padding: "22px 24px", boxShadow: "0 10px 28px rgba(0,80,150,0.16)" },
  eyebrow: { margin: 0, color: "rgba(255,255,255,0.85)", fontWeight: 800, fontSize: "11px", letterSpacing: "0.09em", textTransform: "uppercase" },
  title: { margin: "5px 0", fontSize: "29px", color: "#ffffff", lineHeight: 1.2 },
  subtitle: { margin: 0, color: "rgba(255,255,255,0.92)", maxWidth: "950px", lineHeight: 1.5 },
  headerActions: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" },
  roleBadge: { background: "rgba(255,255,255,0.18)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.42)", borderRadius: "999px", padding: "9px 12px", fontWeight: 800 },
  tabBar: { display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" },
  tab: { background: "var(--fm-card)", color: "var(--fm-text)", border: "1px solid var(--fm-border)", borderRadius: "999px", padding: "10px 16px", fontWeight: 800, cursor: "pointer" },
  tabActive: { background: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "999px", padding: "10px 16px", fontWeight: 800, cursor: "pointer" },
  primaryButton: { background: "var(--fm-page-accent)", color: "#ffffff", border: "1px solid #0078d4", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  secondaryButton: { background: "#eef6ff", color: "var(--fm-page-accent)", border: "1px solid #bfdbfe", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  dangerButton: { background: "var(--fm-card)", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  successBox: { border: "1px solid #bbf7d0", background: "#ecfdf5", color: "#047857", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontWeight: 700 },
  errorBox: { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: "12px", padding: "12px", marginBottom: "16px", fontWeight: 700 },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px", marginBottom: "16px" },
  summaryCard: { border: "1px solid var(--fm-border)", borderRadius: "16px", padding: "14px", background: "var(--fm-card)" },
  badge: { float: "right", background: "#e0f2fe", color: "var(--fm-page-accent)", borderRadius: "999px", padding: "4px 8px", fontSize: "12px", fontWeight: 800 },
  panel: { background: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "18px", padding: "16px", boxShadow: "0 3px 13px rgba(15,23,42,0.052)", marginBottom: "14px", minWidth: 0, overflow: "hidden" },
  panelHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap", marginBottom: "14px" },
  editorPanel: { background: "var(--fm-card)", border: "2px solid #0078d4", borderRadius: "18px", padding: "18px", boxShadow: "0 8px 24px rgba(15, 23, 42, 0.14)", marginBottom: "16px" },
  editorTopBar: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "14px", flexWrap: "wrap", marginBottom: "16px" },
  closeButton: { background: "var(--fm-card)", color: "var(--fm-text)", border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 16px", fontWeight: 900, cursor: "pointer" },
  bottomActions: { display: "flex", gap: "12px", justifyContent: "flex-start", alignItems: "center", marginTop: "18px", paddingTop: "14px", borderTop: "1px solid var(--fm-border)" },
  employeeDraftBottomActions: { display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "18px", paddingTop: "14px", borderTop: "1px solid var(--fm-border)", flexWrap: "wrap" },
  rowButton: { background: "var(--fm-page-accent)", color: "#ffffff", border: "none", borderRadius: "10px", padding: "8px 12px", fontWeight: 800, cursor: "pointer" },
  panelTitle: { margin: 0, fontSize: "20px" },
  panelSubtitle: { margin: "4px 0 0", color: "var(--fm-muted)", fontSize: "14px" },
  inlineActions: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" },
  input: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", width: "100%", boxSizing: "border-box" },
  compactInput: { border: "1px solid var(--fm-border)", borderRadius: "10px", padding: "7px 9px", minWidth: "120px", boxSizing: "border-box" },
  select: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", background: "var(--fm-card)", width: "100%", boxSizing: "border-box" },
  textarea: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", minHeight: "120px", width: "100%", boxSizing: "border-box", fontFamily: "monospace" },
  textareaSmall: { border: "1px solid var(--fm-border)", borderRadius: "12px", padding: "10px 12px", minHeight: "58px", width: "100%", boxSizing: "border-box", marginBottom: "12px" },
  editorGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" },
  field: { display: "grid", gap: "6px" },
  checkboxField: { display: "flex", gap: "8px", alignItems: "center", border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "12px", padding: "10px", fontWeight: 800, color: "var(--fm-text)" },
  label: { color: "var(--fm-text)", fontSize: "13px", fontWeight: 800 },
  helperText: { marginTop: "6px", color: "var(--fm-muted)", fontSize: "12px" },
  helperBox: { marginTop: "14px", border: "1px solid #bfdbfe", background: "#eef6ff", color: "var(--fm-page-accent)", borderRadius: "12px", padding: "10px 12px", fontSize: "13px", fontWeight: 700 },
  ctcCardGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "12px" },
  metricCard: { border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "14px", padding: "12px", display: "grid", gap: "6px" },
  warningMetricCard: { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: "14px", padding: "12px", display: "grid", gap: "6px" },
  goodMetricCard: { border: "1px solid #bbf7d0", background: "#ecfdf5", color: "#047857", borderRadius: "14px", padding: "12px", display: "grid", gap: "6px" },
  decisionPanel: { border: "1px solid #c7d2fe", background: "#f8fafc", borderRadius: "18px", padding: "16px", marginTop: "16px", marginBottom: "16px" },
  approvalReviewPanel: { border: "1px solid #99f6e4", background: "#f8fafc", borderRadius: "18px", padding: "16px", marginTop: "16px", marginBottom: "16px" },
  approvalSection: { border: "1px solid var(--fm-border)", background: "var(--fm-card)", borderRadius: "14px", padding: "12px", marginTop: "12px" },
  detailGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "12px", marginTop: "10px", alignItems: "stretch" },
  reviewNotesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "12px", marginTop: "12px" },
  noteBox: { border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "12px", padding: "12px", minWidth: 0 },
  statusPill: { background: "#fef3c7", color: "#92400e", borderRadius: "999px", padding: "6px 12px", fontWeight: 900, fontSize: "12px" },
  decisionGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "12px" },
  recommendationBox: { marginTop: "12px", border: "1px solid #bfdbfe", background: "#eef6ff", color: "var(--fm-page-accent)", borderRadius: "12px", padding: "12px", fontWeight: 800 },
  suggestionBox: { marginTop: "12px", border: "1px solid #fde68a", background: "#fffbeb", color: "#92400e", borderRadius: "12px", padding: "12px" },
  suggestionList: { display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "10px" },
  suggestionPill: { border: "1px solid #f59e0b", background: "var(--fm-card)", borderRadius: "999px", padding: "6px 10px", fontWeight: 800, fontSize: "12px" },
  optionalGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "10px" },
  optionalCard: { display: "grid", gap: "4px", border: "1px solid var(--fm-border)", background: "var(--fm-card)", borderRadius: "14px", padding: "12px", cursor: "pointer" },
  optionalCardSelected: { display: "grid", gap: "4px", border: "2px solid #0078d4", background: "#ecfdf5", borderRadius: "14px", padding: "12px", cursor: "pointer" },
  variableAdjustBox: { marginTop: "12px", border: "1px solid #99f6e4", background: "#f0fdfa", borderRadius: "14px", padding: "12px" },
  variableAdjustGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "12px", marginTop: "10px" },
  componentGroupGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "12px", marginTop: "16px" },
  subPanel: { border: "1px solid var(--fm-border)", background: "var(--fm-card)", borderRadius: "16px", padding: "12px" },
  groupHeader: { display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start", marginBottom: "8px" },
  groupTitle: { margin: 0, fontSize: "16px" },
  groupSubtitle: { margin: "4px 0 0", color: "var(--fm-muted)", fontSize: "12px" },
  pill: { background: "#e0f2fe", color: "var(--fm-page-accent)", borderRadius: "999px", padding: "4px 8px", fontSize: "12px", fontWeight: 900 },
  emptyBox: { border: "1px solid var(--fm-border)", background: "#f8fafc", borderRadius: "12px", padding: "18px", color: "var(--fm-muted)", textAlign: "center" },
  tableWrapper: { overflowX: "auto", maxWidth: "100%", border: "1px solid var(--fm-border)", borderRadius: "14px", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "900px", fontSize: "13px" },
  compactTable: { width: "100%", borderCollapse: "collapse", minWidth: "720px", fontSize: "12px" },
  th: { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", background: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap" },
  td: { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", whiteSpace: "nowrap", verticalAlign: "top" },
  emptyCell: { padding: "18px", textAlign: "center", color: "var(--fm-muted)" },
  mutedText: { color: "var(--fm-muted)", fontSize: "12px", marginTop: "4px" },
  checkboxField: { minHeight: "54px", border: "1px solid #cfe0ff", background: "#f8fbff", borderRadius: "14px", padding: "12px 14px", display: "flex", alignItems: "center", gap: "12px", justifyContent: "flex-start", cursor: "pointer" },
  checkboxInput: { width: "18px", height: "18px", flex: "0 0 auto", margin: 0 },
  checkboxLabel: { fontWeight: 800, color: "var(--fm-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 },
  catalogueFilterRow: { display: "flex", alignItems: "center", gap: "22px", flexWrap: "wrap", marginTop: "10px", marginBottom: "10px" },
  catalogueFilterOption: { display: "inline-flex", alignItems: "center", gap: "8px", cursor: "pointer", fontWeight: 800, color: "var(--fm-text)", whiteSpace: "nowrap", lineHeight: 1.2 },
  catalogueFilterLabel: { display: "inline-block", fontWeight: 800, color: "var(--fm-text)", whiteSpace: "nowrap" },
  catalogueGroupBox: { marginTop: "14px", border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "10px", background: "var(--fm-card)" },
  catalogueTableWrapper: { width: "100%", maxHeight: "330px", overflow: "auto", border: "1px solid var(--fm-border)", borderRadius: "12px" },
  approvalDetailItem: { display: "grid", gridTemplateColumns: "minmax(120px, 160px) minmax(0, 1fr)", columnGap: "10px", rowGap: "3px", alignItems: "start", border: "1px solid var(--fm-border)", background: "var(--fm-card)", borderRadius: "12px", padding: "10px 12px", minWidth: 0 },
  approvalDetailLabel: { color: "var(--fm-muted)", fontSize: "12px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.02em", whiteSpace: "nowrap" },
  approvalDetailValue: { color: "var(--fm-text)", fontSize: "14px", fontWeight: 800, lineHeight: 1.35, overflowWrap: "anywhere", minWidth: 0 },
  approvalDetailSubValue: { gridColumn: "2 / 3", color: "var(--fm-muted)", fontSize: "12px", lineHeight: 1.3, overflowWrap: "anywhere" },
  noteText: { margin: "8px 0 0", color: "var(--fm-text)", fontSize: "14px", lineHeight: 1.45, overflowWrap: "anywhere" },
  selectedRow: { background: "#ecfeff" },
};

export default Compensation;
